// BuildLab's low-voltage planning-rate model — the single source of truth for
// ballpark pricing, shared by the public Low-Voltage Budget Estimator
// (LowVoltageBudgetPage.js) and the staff admin proposal builder
// (AdminProposalPage.js / AdminRequestDetailPage.js). Everything here is a
// PLANNING number: early-budget ranges refined by a real site walk, never a
// quote. Change a rate here and every surface that prices from it follows.
import { UNIFI_CATALOG, UNIFI_ACCESS_READER_CATALOG, truePanelControllers } from './unifiCatalog';
import { findCameraCatalogEntry } from './cameraCatalog';

// Ballpark installed cost per dwelling unit (materials + labor), low–high.
export const SCOPES = [
    { id: 'cabling', label: 'Structured cabling (Cat6 / fiber)', low: 350, high: 650 },
    { id: 'wifi', label: 'Managed WiFi & networks', low: 300, high: 700 },
    { id: 'surveillance', label: 'Surveillance / CCTV', low: 200, high: 450 },
    { id: 'access', label: 'Door access control', low: 150, high: 400 },
    { id: 'fls', label: 'Fire & life safety (alarm / notification)', low: 400, high: 900 },
];

export const BUILDING_TYPES = [
    { v: 'lowrise', label: 'Garden / low-rise (2–3 stories)', mult: 1.0 },
    { v: 'midrise', label: 'Mid-rise (4–7 stories)', mult: 1.15 },
    { v: 'highrise', label: 'High-rise (8+ stories)', mult: 1.35 },
];

export const SENIOR_MULT = 1.1;

export const volumeFactor = (units) => (units >= 150 ? 0.9 : units >= 50 ? 0.95 : 1.0);

// BuildLab's own per-unit design & project management fee — same $310/unit
// figure already used in the marketing/ROI pages (portfolioRoi.js's
// CONSULT_FEE_PU), surfaced here so staff can add it as a real scope line on
// a proposal via AdminProposalPage.js.
export const DESIGN_MGMT_FEE_PU = 310;
// Real breakdown of what this fee actually covers — staff-supplied
// 2026-07-29, not a guess (the fee previously had no itemization at all,
// just the flat per-unit price line below). Two categories: the real design
// scopes this fee's design work spans, and the coordination work (with the
// GC/subs) that sits on top of all of them, not tied to any one scope.
export const DESIGN_MGMT_FEE_SCOPES = [
    'Structured Cabling and Low-Voltage',
    'Surveillance System',
    'Door Access Controls',
    'WiFi',
    'Area of Refuge',
    'EV Charging Technologies',
    'Common Areas',
];
export const DESIGN_MGMT_FEE_COORDINATION = 'GC and Sub Coordination with any of the relevant design scopes';

// Which real scope_type(s) each Design Scope label above corresponds to, so
// the client-facing card nav can jump straight to that scope's own tab.
// WiFi / Common Areas both live under this app's single combined
// "networking" (Data/Communications) scope — two cards landing on the same
// tab is accurate to how the proposal is actually structured, not a bug.
// Door Access Controls lists both access_control and door_access since
// either scope_type can be the one an actual proposal used. A label with no
// matching scope in a given proposal renders as a plain, non-clickable chip
// rather than a dead link. EV Charging Technologies pointed at 'networking'
// until 2026-07-30, back when EV charging was only an "Add Alternate"
// attached to a Data/Communications scope (see evChargingRecommendation()'s
// own comment below) — now that it's its own standalone ev_charging
// scope_type, this card correctly jumps there instead.
export const DESIGN_MGMT_FEE_SCOPE_TARGETS = {
    'Structured Cabling and Low-Voltage': ['structured_cabling'],
    'Surveillance System': ['surveillance'],
    'Door Access Controls': ['access_control', 'door_access'],
    'WiFi': ['networking'],
    'Area of Refuge': ['area_of_refuge'],
    'EV Charging Technologies': ['ev_charging'],
    'Common Areas': ['networking'],
};

// Extracted so the client-facing card-nav presentation (ClientProposalPage.js,
// ProposalSharePage.js) can render just this line + DesignScopeCardNav.js's
// interactive cards, without parsing it back out of the flat stored text
// below.
export function designManagementFeeLine(units) {
    return units
        ? `BuildLab design & project management fee — $${DESIGN_MGMT_FEE_PU}/unit × ${units} units.`
        : `BuildLab design & project management fee — $${DESIGN_MGMT_FEE_PU} per unit.`;
}

// Single source of truth for this fee's FLAT description text — used when
// staff first adds the scope (AdminProposalPage.js's "+ Add design &
// management fee" button), shown as-is in the admin builder's own textarea
// and in the print-styled proposal document (ProposalDocumentsPage.js),
// where a plain bullet list is exactly right. The two interactive
// client-facing pages render the fee line + Coordination Scope text plus
// DesignScopeCardNav.js's clickable cards instead of this flat text, so
// Design Scopes only ever has one interactive presentation, not a
// duplicate-and-diverge risk between text and cards.
export function designManagementFeeText(units) {
    const designLines = DESIGN_MGMT_FEE_SCOPES.map((s) => `• ${s}`).join('\n');
    return `${designManagementFeeLine(units)}\n\nDesign Scopes:\n${designLines}\n\nCoordination Scope:\n• ${DESIGN_MGMT_FEE_COORDINATION}`;
}

// Per-proposal on/off state for the 7 Design Scopes above — not every
// proposal's coordination fee actually spans all 7 (e.g. a project with no
// EV charging scope), so staff can toggle a label off rather than the card
// nav always showing every catalog entry regardless of what this specific
// project includes. Stored keyed by the label itself (not a separate id) —
// DESIGN_MGMT_FEE_SCOPES is a fixed, rarely-changed catalog, so the label
// IS a stable key here, same simplification already used for
// DESIGN_MGMT_FEE_SCOPE_TARGETS above. Defaults an UNSET label to
// enabled=true (not false, unlike designMgmtMediaAddonsFor's addons below)
// — every Design & Management scope created before this toggle existed
// already implicitly included all 7, so defaulting to "off" would make
// every pre-existing proposal appear to have dropped its entire design
// scope list the moment this feature shipped.
export function designScopeTogglesFor(toggles) {
    const raw = Array.isArray(toggles) ? toggles : [];
    return DESIGN_MGMT_FEE_SCOPES.map((label) => {
        const existing = raw.find((t) => t && t.label === label);
        return { label, enabled: existing ? !!existing.enabled : true };
    });
}

// Fixed catalog of optional media/marketing add-on services staff can toggle
// per Design & Management scope — a distinct discipline (drone/photo
// production) from the low-voltage design scopes above, so kept as its own
// catalog rather than folded into DESIGN_MGMT_FEE_SCOPES. No catalog price
// exists for these yet (staff enters a real number per proposal once
// confirmed) — never default these to a guessed $ figure.
export const DESIGN_MGMT_MEDIA_ADDONS = [
    { key: 'monthly_drone', label: 'Monthly Drone Photo/Video' },
    { key: 'quarterly_teaser', label: 'Quarterly Media Teaser Progress Videos' },
    { key: 'final_aerial_package', label: 'Final Aerial, Elevation & Interior Photos' },
];

// Normalizes a scope's raw media_addons jsonb against the fixed catalog
// above — fills in any catalog item missing from the stored array (e.g. a
// brand-new proposal, or a catalog item added after this scope was created)
// as disabled/unpriced, and drops any stale/unknown keys. Shared by the
// admin builder (renders every catalog item, enabled or not, for toggling)
// and the client-facing pages (which filter this down to enabled-only).
export function designMgmtMediaAddonsFor(mediaAddons) {
    const raw = Array.isArray(mediaAddons) ? mediaAddons : [];
    return DESIGN_MGMT_MEDIA_ADDONS.map((item) => {
        const existing = raw.find((a) => a && a.key === item.key);
        return { key: item.key, label: item.label, enabled: !!existing?.enabled, price: existing?.price ?? null };
    });
}

// The exact math the public estimator page has always run — extracted, not changed.
export function computeBudget({ units, type, senior, picked }) {
    const typeMult = (BUILDING_TYPES.find((t) => t.v === type) || {}).mult || 1;
    const seniorMult = senior ? SENIOR_MULT : 1;
    const vol = volumeFactor(units);
    let lo = 0, hi = 0;
    SCOPES.forEach((s) => { if (picked[s.id]) { lo += s.low; hi += s.high; } });
    const mult = typeMult * seniorMult * vol;
    const puLo = lo * mult;
    const puHi = hi * mult;
    return {
        perUnitLow: puLo,
        perUnitHigh: puHi,
        totalLow: puLo * units,
        totalHigh: puHi * units,
        anyScope: lo > 0,
    };
}

// Scope types with a real interactive floor-plan/device feature (as opposed
// to a plain price line) — the single source every gate in the admin/client
// portal imports instead of repeating `scope_type === 'surveillance'` (or
// forgetting to also check 'access_control'/'networking'/'area_of_refuge')
// independently.
// networking gets its OWN separate floor plan upload for placing WiFi access
// points — not shared with surveillance/access_control's floor plans, even
// when they cover the same physical building. area_of_refuge is the same
// deal — its own floor plan, restricted to refuge_station devices only (see
// the enforce_camera_only_surveillance_device DB trigger).
export const FLOORPLAN_SCOPE_TYPES = ['surveillance', 'access_control', 'networking', 'area_of_refuge'];

// ---------------------------------------------------------------------------
// Flat reseller margin applied to real, cleanly-itemized hardware costs
// across every scope — a staff-directed pricing policy (confirmed 2026-07-29:
// "Across the board the pricing should be Price + 20% for hardware across all
// scopes"), not a researched/sourced number like the vendor prices it
// multiplies. Applied at every point a hardware $ figure is genuinely
// separable from labor: camera/WAP/catalog-modeled-reader device hardware
// (estimateDeviceCost), the generic surveillance head-end equipment estimate
// (estimateDeviceCost's headEnd), rack_items equipment (rackBuilderSummary —
// covers switches/routers/NVRs/storage/AOR head-end/access-control panels/
// anything staff enters, freeform or catalog-picked, per explicit direction),
// EV charger hardware (evChargingRecommendation), surveillance storage drives
// (surveillanceStorageEstimate), and the real Legrand SMC panel material cost
// (smcPanelAlternate, applied to SMC_PANEL_OPTIONS[].material only, not the
// install-labor portion folded into its returned price).
//
// Deliberately NOT applied to numbers with no sourced hardware/labor split:
// - The legacy flat DEVICE_RATES for a reader/intercom placed with no
//   specific hardware model (and the generic DEVICE_RATES.other fallback) —
//   these are documented "all-in" figures that already bundle strike/REX
//   wiring or similar labor into one historical planning number. Marking
//   these up would silently inflate their embedded labor too, which this
//   markup must never touch. (refuge_station/refuge_commander left this
//   list 2026-07-29 and panel followed 2026-07-30 — each now carries a real
//   {hardware, labor} split, so the markup applies to their clean hardware
//   portion like every other decomposed figure; see each entry's own
//   comment in DEVICE_RATES below.)
// - Every CABLING_RATES-derived figure (cablingTakeoff's in-unit/pathway/coax/
//   MPOE rates, commonAreaDropsCost, coaxHomerunAlternate) — CABLING_RATES is
//   explicitly documented as "installed (materials + labor)", a blended flat
//   planning rate with no decomposition available.
// Once a real per-item hardware/labor split exists for those categories (the
// way it already does for cameras, WAPs, and catalog-modeled readers), they
// can adopt this same constant.
export const HARDWARE_MARKUP = 1.20;

// Per-scope markup override (real staff request 2026-07-30: "I need an
// override toggle for Markup percentage on Equipment Pricing with a field
// of percentage which will update the Equipment Schedule Unit Price
// Automatically") — buildlab_proposal_scopes.equipment_markup_pct, a
// nullable numeric column: null = keep the global HARDWARE_MARKUP default;
// a value like 15 means 15%. The pricing functions below that apply
// HARDWARE_MARKUP to a clean per-item hardware figure (rackBuilderSummary,
// deviceLineCostComputed and its wrappers) take an optional trailing
// markupPct and resolve it through this ONE helper, so "what a pct means"
// (a whole-number percentage, never a raw multiplier) can't drift between
// call sites. Anything non-numeric — null, undefined, '' — falls back to
// HARDWARE_MARKUP, so every pre-existing caller (none of which pass a pct)
// stays byte-identical. The deliberate exclusions listed above (legacy
// "all-in" DEVICE_RATES, CABLING_RATES-derived figures) are unaffected —
// a custom pct only ever moves figures the flat 20% already moved.
export const markupMultiplier = (pct) =>
    (pct != null && pct !== '' && !Number.isNaN(Number(pct)) ? 1 + Number(pct) / 100 : HARDWARE_MARKUP);

// ---------------------------------------------------------------------------
// Device-level rates — used by the admin builder to price a surveillance or
// door-access scope from the ACTUAL devices placed on its floor plans,
// instead of the generic per-unit range. Installed cost each (hardware +
// mounting + termination), planning-level assumptions consistent with the
// per-unit model above. Every non-camera device type is itself a per-subtype
// object (mirroring `camera`'s shape) with its own `other` fallback, used
// when a device predates subtype tracking or has no matching subtype.
// Real cited industry labor rate, not BuildLab's own — IPVM's own published
// Security Integrator Hourly Rate surveys (2013: median $90-100/hr; 2016:
// median $100-125/hr, install techs specifically $75-110/hr) converge on
// $100/hr as a defensible median. Replace this with BuildLab's own actual
// billed rate once known — every camera labor figure below scales off it.
export const CAMERA_LABOR_RATE_PER_HOUR = 100;
export const DEVICE_RATES = {
    // bullet/dome/ptz/panoramic_180/fisheye_360/multi_sensor: real hardware
    // pricing supplied directly by BuildLab staff, tied to the UniFi Protect
    // G6 camera line — G6 Bullet $199, G6 Dome $279, G6 PTZ $399, G6 180
    // $299, G6 Pro 360 $499, multi_sensor $1,799 (supersedes an earlier
    // multi_sensor entry derived from third-party Axis Q6100-E/Hanwha
    // PNM-9084RQZ distributor research — staff-supplied real pricing takes
    // precedence once it exists). Labor is now time-based (hours x
    // CAMERA_LABOR_RATE_PER_HOUR), NOT the flat 20%-of-hardware markup this
    // used before 2026-07-28 — that convention was never researched and,
    // checked against real IPVM labor-hour standards and market installed-
    // cost data, turned out to underprice camera labor by roughly 2-4x
    // (worst for PTZ, where a hardware-pegged markup gave almost no credit
    // for the extra preset/tour commissioning PTZ genuinely requires). Hours
    // come from IPVM's own published standards: Camera Commissioning Labor
    // Standards (0.5hr preconfig + 0.59hr aim & focus average per camera,
    // ~1.1hr) and Camera Labor Estimation Standard 2014 (mounting 0.95hr
    // easy-indoor to 3.5hr outdoor-masonry — explicitly brand/price-agnostic
    // per IPVM's own forum consensus, i.e. NOT meant to vary by hardware
    // tier the way the old 20% formula did). bullet/dome/fisheye_360/
    // panoramic_180 use the "easy mount" tier (0.95 + 1.1 = 2.05hr) since no
    // source differentiates base commissioning time by camera type, only by
    // mounting difficulty. **ptz was originally priced at IPVM's "hardest
    // mount" tier (3.5hr) plus its high-end aim & focus figure (3.5hr) — 7.5hr
    // ($750) total, reasoning that preset/tour commissioning genuinely takes
    // longer than a fixed camera. Overridden 2026-07-30 per explicit staff
    // direction ("PTZ should not be 750 but the same 205") back down to the
    // same 2.05hr easy-mount tier as bullet/dome/fisheye_360/panoramic_180** —
    // real BuildLab install experience doesn't bear out the extra
    // preset/tour time IPVM's general standard assumed, so this now matches
    // every other single-lens subtype rather than a theoretical derivation.
    // multi_sensor
    // repeats the aim & focus step once per independently-aimed sensor (4,
    // its common real housing configuration for this camera class) on top
    // of the easy-mount tier: 2.5hr mount + 0.5hr preconfig + 4x0.59hr aim &
    // focus = 5.36hr.
    // other: a legacy flat placeholder predating the G6-series repricing —
    // no known hardware/labor split exists for it, so it stays 100%
    // hardware with $0 labor rather than inventing one.
    // doorbell (added 2026-07-30, for residential surveillance-only projects
    // with no access_control scope — see unifiCatalog.js's UNIFI_ACCESS_
    // READER_CATALOG comment for why this had to be a camera subtype rather
    // than reusing the existing UniFi Access "door station" reader/intercom
    // catalog entries there, which require a separate Access Hub this
    // project type doesn't have): real retail pricing, store.ui.com,
    // 2026-07-30 — Ubiquiti UVC-G4-
    // Doorbell-Pro-PoE-Kit, $379, which bundles the G4 Doorbell Pro camera
    // + its required PoE-to-24VAC adapter (the doorbell itself only takes
    // 16-24V AC or USB-C natively, NOT 802.3af/at PoE directly) + a UACC-
    // Chime-PoE indoor chime — a real, single-SKU, install-ready kit rather
    // than three separately-priced parts, chosen over the cheaper native-PoE
    // UVC-Doorbell-Lite ($99, no chime) specifically so the price already
    // includes a way to actually hear the doorbell ring indoors; buying the
    // Lite + a standalone Chime-PoE ($79) separately comes to $178, so the
    // Pro Kit's $379 buys the flagship 2MP/package-detection unit for close
    // to the Lite-plus-chime price, not a premium over it. Labor uses the
    // same "easy mount" 2.05hr tier as bullet/dome/fisheye_360/panoramic_180
    // above (IPVM's mounting + aim & focus standards) — a doorbell is still
    // a single non-PTZ, non-multi-sensor camera install at a fixed, easily
    // reached door-frame height, and the indoor chime's own placement/pairing
    // adds no meaningful extra time to the same visit (no aiming/focusing
    // involved), so it doesn't need its own labor line the way multi_sensor's
    // repeated aim & focus step does.
    camera: {
        bullet: { hardware: 199, labor: 2.05 * CAMERA_LABOR_RATE_PER_HOUR },
        dome: { hardware: 279, labor: 2.05 * CAMERA_LABOR_RATE_PER_HOUR },
        fisheye_360: { hardware: 499, labor: 2.05 * CAMERA_LABOR_RATE_PER_HOUR },
        panoramic_180: { hardware: 299, labor: 2.05 * CAMERA_LABOR_RATE_PER_HOUR },
        ptz: { hardware: 399, labor: 2.05 * CAMERA_LABOR_RATE_PER_HOUR },
        multi_sensor: { hardware: 1799, labor: 5.36 * CAMERA_LABOR_RATE_PER_HOUR },
        doorbell: { hardware: 379, labor: 2.05 * CAMERA_LABOR_RATE_PER_HOUR },
        other: { hardware: 900, labor: 0 },
    },
    reader: { card: 2200, keypad: 1800, biometric: 4500, long_range: 3200, other: 2500 }, // per controlled opening, incl. strike/REX tie-in
    // Controller / power enclosure. Split from flat all-in figures
    // ({ single_door: 1500, multi_door: 3500, power_supply: 900, other: 1800 })
    // into {hardware, labor} on 2026-07-30, following refuge_station's
    // precedent below, in response to a real staff report the same day: "I
    // noticed in the access control that 1 Single Door Control without
    // manufacturer and model is 1500" — the old flat figures gave no account
    // of what the number was made of, and a panel could never price from a
    // real hardware model the way readers/intercoms already do (fixed via
    // truePanelControllers() + deviceLineCostComputed's new panel branches).
    // `hardware` is anchored to REAL catalog prices — the exact UNIFI_CATALOG
    // access_control_panel entries (all confirmed live on store.ui.com
    // 2026-07-27, recent enough to use as-is): UA-Hub-Door $199 single-door
    // controller (single_door), EAH-8 $999 8-door Enterprise Access Hub
    // (multi_door), UACC-Retrofit-PSU-12V $79 (power_supply). `labor` is a
    // reasoned head-end install planning figure, documented as such, NOT a
    // sourced install-hour build-up: each is the legacy all-in figure's
    // residual after the real marked-up hardware anchor is pulled out,
    // rounded — chosen deliberately so an UNMODELED panel's new
    // hardware×markup+labor total stays in the same ballpark as its old flat
    // figure, so pre-existing proposals don't silently reprice. Exact
    // before/after (markup at the default 20%): single_door $1,500 →
    // 199×1.2 + 1,260 = $1,498.80 (−$1.20); multi_door $3,500 → 999×1.2 +
    // 2,300 = $3,498.80 (−$1.20); power_supply $900 → 79×1.2 + 805 =
    // $899.80 (−$0.20). What the labor covers is genuinely head-end-side
    // work — enclosure/DIN-rail mount, power, network uplink, terminating
    // the home-run door wiring at the controller, panel programming and
    // per-door commissioning (hence multi_door's larger figure) — a
    // DIFFERENT scope of work from ACCESS_READER_INSTALL_LABOR's door-side
    // reader bundle (strike/REX/door contact at the controlled opening
    // itself); see the modeled-panel branch in deviceLineCostComputed for
    // why that constant is never reused here. `other` is the legacy
    // catch-all with no real product behind it — hardware is the legacy
    // $1,800 divided by the default 1.20 markup ($1,500) so its marked-up
    // total stays exactly the legacy figure (inflating it 20% because of a
    // refactor would be a silent repricing nobody asked for), with $0 labor
    // per the same "no sourced split" convention as camera.other.
    // LABOR ZEROED 2026-07-30, hours after the split above shipped — real
    // staff pricing policy, verbatim: "The 5870 already includes the labor
    // for all Door Controllers which includes the single hub." The
    // residual labor figures the split introduced (1260/2300/805 —
    // engineered from the legacy flat totals) double-charged controller
    // work that ACCESS_HEADEND_PROGRAMMING_LABOR's flat $5,870 fee already
    // owns (its own comment: controller configuration, hardware/software
    // integration, credential onboarding). A panel device now contributes
    // HARDWARE ONLY (real catalog anchor × markup); every hour of
    // controller labor — mounting, power, termination, programming, for
    // rack-mounted EAH-8s and door-local single hubs alike — bills once,
    // through the flat head-end fee. Consequence, per that same policy: an
    // unmodeled single_door panel totals $238.80 (hardware × markup), not
    // the old $1,498.80 — the deliberate opposite of the "keep totals in
    // the same ballpark" goal the hours-earlier split had, because staff
    // identified the ballpark itself as a double charge.
    panel: {
        single_door: { hardware: 199, labor: 0 },
        multi_door: { hardware: 999, labor: 0 },
        power_supply: { hardware: 79, labor: 0 },
        other: { hardware: 1500, labor: 0 },
    },
    // Entry intercom station, priced by mounting style — pedestal/gooseneck
    // cost more than a flush wall unit because of the post footing/concrete
    // base and the extra conduit run out to a stand-alone location (e.g. a
    // gated driveway), not because of any audio/video capability difference.
    intercom: { surface: 1800, recessed: 2200, pedestal: 3200, gooseneck: 3600, other: 2400 },
    // wap has no subtype column (see FloorplanEditorCanvas.js's WAP_ADD_SPECS)
    // — real per-model pricing comes from UNIFI_CATALOG via wap_model instead
    // (see estimateDeviceCost below). `other` here is only the placeholder
    // used before staff picks a model on a freshly-placed AP.
    wap: { other: 350 },
    // RATH SmartCommand AOR call station, installed — box, termination,
    // programming, AND a per-location signage kit (see AREA_OF_REFUGE_SIGNAGE
    // below — IBC 1009.6.5 requires signage at every area-of-refuge location,
    // so it scales 1:1 with call stations instead of needing its own
    // floor-plan marker), on top of real hardware cost. The home-run cable
    // back to the BOSS 3400 head-end (two rack_items lines, not a floor-plan
    // device — see AREA_OF_REFUGE_CONTROL_MODULE/_CALL_COMMANDER below) is
    // NOT bundled into this labor figure (was, until 2026-07-29 — see
    // estimateDeviceCost's cabling comment for why that changed); it's priced
    // via the standard CABLE_PER_DEVICE rate like every other non-camera
    // device, so it actually shows up in the Running Total's Cabling line
    // instead of hiding inside Labor with no visible per-station scaling.
    // `standard` = 3300SS hardware ($633.65) + signage kit ($75.40 + $62.35
    // = $137.75) + $552 install/box/programming labor (a reasoned planning
    // estimate, not a sourced number — carried over unchanged from this
    // line's Cornell-era figure, since no per-install-hour research exists
    // for this trade the way camera install labor got in the 2026-07-28
    // pricing pass). `weatherproof` = 3300S4 hardware ($646.70) + the same
    // signage kit + a flat $850 labor total (NOT standard's $552 plus an
    // $850 add-on — $850 is the weatherproof line's whole labor figure,
    // reflecting the real extra outdoor weatherproof-sealing/vandal-
    // resistant-mounting work over standard's install), a reasoned premium,
    // not a sourced number, same convention as the Cornell-era figure it
    // replaces (that one had no real hardware anchor at all, since Cornell's
    // weatherproof SKU had no confirmed public price; RATH's 3300S4 does, a
    // real improvement in how grounded this number now is).
    // Split into {hardware, labor} (2026-07-29 — was a single flat number
    // per subtype, which silently routed the ENTIRE figure through
    // estimateDeviceCost's generic hardware-only branch, so a refuge_station
    // never contributed anything to the Running Total's Labor line even
    // though $552/$850 of real labor is baked into these numbers — a
    // confirmed user-reported bug, fixed by exposing the same split the
    // comment above already documents in full. `hardware` folds in the
    // per-location signage kit (AREA_OF_REFUGE_SIGNAGE: $75.40 + $62.35 =
    // $137.75) alongside the real call-station hardware, since both are
    // physical product cost with no labor content — standard: $633.65
    // (3300SS) + $137.75 = $771.40; weatherproof: $646.70 (3300S4) + $137.75
    // = $784.45. `other` has no sourced split (legacy/unspecified subtype),
    // same "100% hardware, $0 labor" treatment as camera.other.
    refuge_station: {
        standard: { hardware: 771.40, labor: 552 },
        weatherproof: { hardware: 784.45, labor: 850 },
        other: { hardware: 1300, labor: 0 },
    },
    // The Call Commander operator console — where staff/security actually
    // answer AOR calls, RATH 3200D (desk-mount) by default, real hardware
    // ~$639 — is already priced as a rack_items line (see
    // AREA_OF_REFUGE_CALL_COMMANDER), since the head-end seed button needs a
    // price before any floor plan exists. This is the SAME console's
    // floor-plan marker, added 2026-07-29 so staff can show WHERE it
    // physically sits (front desk, security office, fire command room) —
    // hardware stays $0 here on purpose, so placing it never double-counts
    // the rack_items line. $350 is a reasoned labor estimate (console
    // mounting + pairing/commissioning + testing against the BOSS 3400 head-
    // end), same "not a sourced number" convention refuge_station's own
    // labor figure already uses, since no per-install-hour source exists for
    // this task either. Cabling is NOT special-cased to $0 the way
    // refuge_station's is — it falls into the generic CABLE_PER_DEVICE
    // average below like every other non-camera device, which is real-world
    // reasonable here: RATH's own SmartCommand manual specs plain 18/4 cable
    // for the Call-Commander-to-BOSS run (confirmed non-fire-rated 18/4
    // CMR/CL3R cable price: $0.35/ft, Platt Electric Supply, 2026-07-29),
    // so $250 covers a genuinely typical run's material + pull labor without
    // needing an invented distance assumption. If the AHJ requires 2-hour
    // fire-rated cable instead, add it as the same manual freeform rack-item
    // row already used for refuge_station's fire-rated wiring — see the
    // "Fire-rated cable" note in CLAUDE.md; this was deliberately never
    // turned into a flat default there either, for the same reason.
    // Split into {hardware, labor} (2026-07-29, same fix/reasoning as
    // refuge_station above) — hardware stays $0 (real hardware is priced via
    // the AREA_OF_REFUGE_CALL_COMMANDER rack_items line, see the comment
    // above), all $350 is labor, so it now actually reaches the Running
    // Total's Labor line instead of silently vanishing into the old generic
    // hardware-only branch.
    refuge_commander: { commander: { hardware: 0, labor: 350 } },
    other: 500,
};
// Real product photos for the client-facing device gallery (DeviceInspector.js),
// keyed by camera_subtype — one array per UniFi Protect G6 model priced above:
// G6 Bullet (bullet), G6 Dome (dome), G6 PTZ (ptz), G6 180 (panoramic_180),
// G6 Pro 360 (fisheye_360). All URLs point directly at Ubiquiti's own CDN
// (cdn.ecomm.ui.com), sourced the same way RackElevation's staff-pasted
// image_url fields already are — real reseller-authorized CDN links, never
// scraped/downloaded/embedded by this codebase itself. `other` and
// `multi_sensor` have no entry: `other` is a legacy catch-all with no single
// real product behind it, and multi_sensor's only researched candidate
// (Hanwha Wisenet PNM-9084RQZ) is a discontinued SKU per Hanwha's own product
// page, so it's omitted rather than shown as if it were current hardware.
// Every model here is confirmed (via store.ui.com's own per-color product
// galleries) to ship in both White and Black — each array's final 1-2 entries
// are clean, unambiguous single-color shots for whichever color(s) weren't
// already clearly shown by an earlier angle; see CAMERA_PHOTO_CAPTIONS in
// DeviceInspector.js (index-matched to these arrays) for which entry is which.
// `doorbell`'s single entry is the real UVC-G4-Doorbell-Pro-PoE-Kit product
// shot (the exact SKU priced in DEVICE_RATES.camera.doorbell above) — only
// one confirmed angle exists for this one, unlike the multi-angle G6 arrays.
export const CAMERA_SUBTYPE_IMAGES = {
    bullet: [
        'https://cdn.ecomm.ui.com/products/5dc40311-0e08-4eaa-b901-472cc707b436/c7746e68-2298-43ce-9793-ea8b1bc20f97.png',
        'https://cdn.ecomm.ui.com/products/5dc40311-0e08-4eaa-b901-472cc707b436/4dee8c08-78b7-418f-ab85-01329267c062.png',
        'https://cdn.ecomm.ui.com/products/5dc40311-0e08-4eaa-b901-472cc707b436/2a461dea-6642-437e-b57a-27fbe94ed8fc.png',
        'https://cdn.ecomm.ui.com/products/5dc40311-0e08-4eaa-b901-472cc707b436/7769ee13-3a54-43bc-93d1-d26ad9d9d0d0.png',
    ],
    dome: [
        'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/177298b2-ba1d-479f-a16f-172e4eb0970a.png',
        'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/b50e77bc-19c0-45e1-a841-3398cef8920a.png',
        'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/b698a9af-5f9f-434c-8d58-eafbd44af59a.png',
        'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/1e661d71-79eb-4f79-9f17-2199b3655bb4.png',
        'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/c72da83a-b59d-4339-b597-29d2f72b37e8.png',
        'https://cdn.ecomm.ui.com/products/2f241968-e3be-42c2-98fc-7d879b360d25/07eef437-6bab-4c19-80d4-206b5a5d0634.png',
    ],
    ptz: [
        'https://cdn.ecomm.ui.com/products/8b0777b6-851e-45f8-b4d2-b98b598c1ebc/e38c2682-4417-4e12-8a55-71c8cbc2b45d.png',
        'https://cdn.ecomm.ui.com/products/8b0777b6-851e-45f8-b4d2-b98b598c1ebc/c7eb0b7a-aa04-4c60-b300-d7d7419ba04e.png',
        'https://cdn.ecomm.ui.com/products/8b0777b6-851e-45f8-b4d2-b98b598c1ebc/2523c4e6-4a8d-4547-8c8f-708fd3c27165.png',
        'https://cdn.ecomm.ui.com/products/8b0777b6-851e-45f8-b4d2-b98b598c1ebc/7de02d58-621c-4ca8-8b2d-3a2ba07fb15d.png',
        'https://cdn.ecomm.ui.com/products/8b0777b6-851e-45f8-b4d2-b98b598c1ebc/99ca2edb-f32a-405b-919e-572015028359.png',
    ],
    panoramic_180: [
        'https://cdn.ecomm.ui.com/products/c2196251-739c-43d0-b840-567a732cd7ef/d6585481-ad64-4563-9b1e-5a0a0822613c.png',
        'https://cdn.ecomm.ui.com/products/c2196251-739c-43d0-b840-567a732cd7ef/05c490b3-617a-4400-aad5-9902a34c8905.png',
        'https://cdn.ecomm.ui.com/products/c2196251-739c-43d0-b840-567a732cd7ef/82cf13fe-95ba-47bf-b2a1-9178866544c2.png',
        'https://cdn.ecomm.ui.com/products/c2196251-739c-43d0-b840-567a732cd7ef/fb06b1af-c961-41ee-a5c8-c862f6e388ad.png',
        'https://cdn.ecomm.ui.com/products/c2196251-739c-43d0-b840-567a732cd7ef/4e7f3df7-b623-488d-ada1-c44305fda8f4.png',
    ],
    fisheye_360: [
        'https://cdn.ecomm.ui.com/products/43852c61-28db-41fd-8907-c283fffa41af/50fc1f10-c18b-4c1a-913d-59d2b616f558.png',
        'https://cdn.ecomm.ui.com/products/43852c61-28db-41fd-8907-c283fffa41af/c7a4264b-745b-40e8-bcfb-2e607225b926.png',
        'https://cdn.ecomm.ui.com/products/43852c61-28db-41fd-8907-c283fffa41af/c9ed93ec-862f-487f-b7b4-d5adff168b08.png',
        'https://cdn.ecomm.ui.com/products/43852c61-28db-41fd-8907-c283fffa41af/f39cb083-0114-4876-afc2-ef8da253a549.png',
        'https://cdn.ecomm.ui.com/products/43852c61-28db-41fd-8907-c283fffa41af/11836f28-cc0a-4369-aea3-a666c889944b.png',
    ],
    doorbell: [
        'https://cdn.ecomm.ui.com/products/217c2677-ceb5-4908-8953-792cdc72fd32/d5552edd-a6a5-4cbc-8fec-e05070faa882.png',
    ],
};
// Real, staff-supplied sample livestream links (UniFi Protect public share,
// monitor.ui.com) for a camera subtype's client-facing device details — lets
// a client see live footage from that camera type before it's installed on
// their own site. Only populated where a real link has been provided; a
// subtype with no entry here simply shows no livestream link (same
// never-fabricate convention as every other reference URL in this file).
//
// UniFi Protect share links are time-limited by UniFi itself, not by this
// app — the fisheye_360 link below carries a real, staff-stated expiration
// (Sharing expires at 7/30/2026, 06:37am) that this codebase has no way to
// detect or enforce (it's just a URL string). When this link goes dead,
// swap in a fresh share link from staff rather than leaving a broken one
// live — same "always re-verify before it reaches a client" convention as
// every distributor price in this file.
export const CAMERA_SUBTYPE_LIVESTREAM_URL = {
    fisheye_360: 'https://monitor.ui.com/2455aaeb-d066-48de-b413-bd158b32da5f', // expires 7/30/2026, 06:37am — replace before then
    bullet: 'https://monitor.ui.com/37a925c1-a1a1-4bab-90de-5e69e4cf3313', // expires 7/30/2026, 06:37am — replace before then
    dome: 'https://monitor.ui.com/1412c168-e000-4bce-b720-8bcb7d7a6a3b', // expires 7/30/2026, 06:37am — replace before then
};
// Real product photos for the client-facing device gallery, for Access
// Control devices — keyed by `${device_type}:${access_subtype}` (device_type
// is needed because the same access_subtype strings are shared across
// reader/panel/intercom, see ACCESS_SUBTYPE_LABEL below). Every URL was
// personally fetched and confirmed HTTP 200 / image/png / visually correct
// (not a placeholder/HTML/icon) before being added here — same standard as
// CAMERA_SUBTYPE_IMAGES above. All 11 access_subtype combinations that exist
// in the DB CHECK constraint resolved to at least one confirmed real product,
// so none are skipped:
//  - reader:card/keypad/biometric are UniFi Access's own Reader / Reader Flex
//    / G3 Reader Fingerprint (cdn.ecomm.ui.com).
//  - reader:long_range has NO UniFi Access product (confirmed against
//    store.ui.com's full reader lineup — card/flex/fingerprint are the only
//    reader SKUs) — falls back to Nedap Identification Systems' "uPASS Go"
//    long-range UHF vehicle reader, a real product on Nedap's own CDN.
//  - panel:single_door/multi_door/power_supply are UniFi Access's Door Hub,
//    Enterprise Access Hub (EAH-8), and Retrofit PSU 12V.
//  - intercom:surface/recessed are UniFi Access's Intercom (+ the official
//    Intercom Flush Mount accessory for `recessed`).
//  - intercom:pedestal/gooseneck have no standalone UniFi product photo —
//    confirmed real (Ubiquiti's own gooseneck/pedestal adapter ships in-box
//    but is never separately photographed anywhere in their catalog, per
//    techspecs.ui.com) — falls back to Pedestal PRO, a real US manufacturer
//    of access-control pedestal enclosures, using their SKUs built
//    specifically to fit the UniFi/Ubiquiti intercom.
export const ACCESS_SUBTYPE_IMAGES = {
    'reader:card': [
        'https://cdn.ecomm.ui.com/products/f20970ca-1951-4ed9-9016-3eea65981a94/b36edf90-78d4-43fc-b818-172dbc1ce079.png',
        'https://cdn.ecomm.ui.com/products/f20970ca-1951-4ed9-9016-3eea65981a94/981bd0c8-da47-45c6-b185-34bb93d5878a.png',
        'https://cdn.ecomm.ui.com/products/f20970ca-1951-4ed9-9016-3eea65981a94/be4c04c1-bf15-4bae-aacd-2bbc9d1a095c.png',
    ],
    'reader:keypad': [
        'https://cdn.ecomm.ui.com/products/21cddd36-86bb-4001-b9c5-cb501a509688/5378771f-ecb6-4252-94fc-774b30fdff93.png',
        'https://cdn.ecomm.ui.com/products/21cddd36-86bb-4001-b9c5-cb501a509688/f74f0fc7-03e0-4ccb-a185-fb6204855f6c.png',
    ],
    'reader:biometric': [
        'https://cdn.ecomm.ui.com/products/082b17ac-1af2-4474-b406-b6b23d59440b/659d5a32-4c07-4dc3-94f3-6cec1b50e0db.png',
        'https://cdn.ecomm.ui.com/products/082b17ac-1af2-4474-b406-b6b23d59440b/7c000663-7704-4782-85db-2d11eceea3df.png',
    ],
    'reader:long_range': [
        'https://www.nedapidentification.com/wp-content/uploads/2024/05/uPASS-Go-lr-front-blue-1-e1715605026540.png',
        'https://www.nedapidentification.com/wp-content/uploads/2024/05/uPASS-Go-lr-diagonal-blue-e1715605060149.png',
        'https://www.nedapidentification.com/wp-content/uploads/2024/05/uPASS-Go-lr-side-blue-e1715605103701.png',
    ],
    'panel:single_door': [
        'https://cdn.ecomm.ui.com/products/90d00ca5-015c-4bc6-b3bd-0d1f8d5bdea3/d37fb6e1-1036-459c-a00c-3b7b2c8cda56.png',
        'https://cdn.ecomm.ui.com/products/90d00ca5-015c-4bc6-b3bd-0d1f8d5bdea3/ef1a07e1-a9e5-434a-9881-205ae73c8d90.png',
    ],
    'panel:multi_door': [
        'https://cdn.ecomm.ui.com/products/2442d7f0-0126-4279-b838-0baa8be294fb/61fdf4fd-3f08-460c-8089-4df208ff7af2.png',
        'https://cdn.ecomm.ui.com/products/2442d7f0-0126-4279-b838-0baa8be294fb/ea8d00b3-bb52-4302-b50b-e173d51dbef2.png',
    ],
    'panel:power_supply': [
        'https://cdn.ecomm.ui.com/products/6278fb7c-a460-448f-afc8-02ed1d14f718/82f2fa1c-1d7f-4b8e-83b6-9c939f76e8f8.png',
        'https://cdn.ecomm.ui.com/products/6278fb7c-a460-448f-afc8-02ed1d14f718/857d4b59-8c22-47d1-b3b1-0e6e8d3e2a71.png',
    ],
    'intercom:surface': [
        'https://cdn.ecomm.ui.com/products/c113610f-30f0-4230-8545-5a038782ab66/e0461ba7-d924-40ae-9296-4d1170f6dd4a.png',
        'https://cdn.ecomm.ui.com/products/c113610f-30f0-4230-8545-5a038782ab66/dc60325b-09eb-488b-a60e-4aa55a5cf4cb.png',
    ],
    'intercom:recessed': [
        'https://cdn.ecomm.ui.com/products/89e099da-fa9f-43ce-9059-8f998a3fbbdd/6e6101e7-867f-478e-b977-96bf0659995b.png',
        'https://cdn.ecomm.ui.com/products/89e099da-fa9f-43ce-9059-8f998a3fbbdd/0d5e81d4-6f87-4d6c-b126-314a2115a64b.png',
    ],
    'intercom:pedestal': [
        'https://pedestalpro.com/storage/products/818hou-ubqi-01-crs/818hou-ubqi-01-crs_Front_1768930042.png',
        'https://pedestalpro.com/storage/products/84tow-ubqi-01-304/84tow-ubqi-01-304_Front_1768930058.png',
    ],
    'intercom:gooseneck': [
        'https://pedestalpro.com/storage/products/36-9c/36-9c_Perspective.png',
        'https://pedestalpro.com/storage/products/42-9c-blk/42-9c-blk_perspective_1760562967.png',
    ],
};
// Real product photos for the client-facing device gallery, for WiFi access
// points — keyed by the exact `wap_model` string stored on the device (the
// same UNIFI_CATALOG model key an AP's price is already resolved from). Every
// URL is pulled directly from that model's own store.ui.com product gallery
// (cdn.ecomm.ui.com), confirmed HTTP 200 / image/png / visually correct for
// that specific model — same standard as CAMERA_SUBTYPE_IMAGES above. Note:
// U7-Pro/U6-Pro/U7-Pro-Max's first image is byte-identical across all three
// (Ubiquiti's own CDN reuses one hero render for visually-near-identical
// sibling AP housings) — still a real photo of that product line, just not a
// per-model-unique angle for that one slot; each model's other 2 images are
// genuinely distinct. This is INTENTIONALLY NOT exhaustive of every model in
// UNIFI_CATALOG (33 real wap_controller models exist there) — only this
// researched subset has verified photo sets so far. A future session can
// extend this the same way: pull a model's own gallery array (in DOM order)
// from its store.ui.com product page and confirm each URL live before adding.
export const WAP_MODEL_IMAGES = {
    'U7-Pro': [
        'https://cdn.ecomm.ui.com/products/fa8dd4e4-36c8-4c79-a928-22c7bff2ce29/ab5bc8a4-6135-402e-a695-e3ea5e16d3e6.png',
        'https://cdn.ecomm.ui.com/products/fa8dd4e4-36c8-4c79-a928-22c7bff2ce29/513b42b4-666b-4a72-9bbc-efdc41daf360.png',
        'https://cdn.ecomm.ui.com/products/fa8dd4e4-36c8-4c79-a928-22c7bff2ce29/08c8d5d7-657f-40cb-8c29-1d9d911d9bfb.png',
    ],
    'U7-LR': [
        'https://cdn.ecomm.ui.com/products/7455fa2b-3074-47a0-b82f-a2cd701d4a8f/06752627-8180-42ea-afe3-4cfac57c10eb.png',
        'https://cdn.ecomm.ui.com/products/7455fa2b-3074-47a0-b82f-a2cd701d4a8f/1c7847ac-056c-4f14-abab-b6576d58fe86.png',
        'https://cdn.ecomm.ui.com/products/7455fa2b-3074-47a0-b82f-a2cd701d4a8f/bf8a8ffd-ecb9-4d78-8bb4-85bee252d0bd.png',
    ],
    'U6+': [
        'https://cdn.ecomm.ui.com/products/6d5c6141-e2e9-416a-b789-53e59416bb1a/853bc73b-f65b-4e59-a171-75c9a4a4615e.png',
        'https://cdn.ecomm.ui.com/products/6d5c6141-e2e9-416a-b789-53e59416bb1a/170dd624-64c1-4b7c-a765-8249068acd33.png',
        'https://cdn.ecomm.ui.com/products/6d5c6141-e2e9-416a-b789-53e59416bb1a/686e61ee-a263-47b1-8e82-2523b7932a7a.png',
    ],
    'U6-Pro': [
        'https://cdn.ecomm.ui.com/products/8e88b222-7a55-4cf0-8677-ae9b6347fe84/e16aa122-b5e5-4ffb-9f1a-27ee14d9ab3d.png',
        'https://cdn.ecomm.ui.com/products/8e88b222-7a55-4cf0-8677-ae9b6347fe84/c00c099f-b7cf-4ce7-877b-6d0c7b71ce79.png',
        'https://cdn.ecomm.ui.com/products/8e88b222-7a55-4cf0-8677-ae9b6347fe84/981a1737-e7e5-45ac-84b8-6140819a1517.png',
    ],
    'U6-Enterprise': [
        'https://cdn.ecomm.ui.com/products/f9118c0f-060b-4fd7-99ce-ace671c7a1fe/6f2a87a4-193d-43e0-b253-aa0ea0795694.png',
        'https://cdn.ecomm.ui.com/products/f9118c0f-060b-4fd7-99ce-ace671c7a1fe/48622508-35fe-4c5e-927e-3804506693da.png',
        'https://cdn.ecomm.ui.com/products/f9118c0f-060b-4fd7-99ce-ace671c7a1fe/da8af82e-a21f-4b9d-870c-06dc06696cc7.png',
    ],
    'U7-Lite': [
        'https://cdn.ecomm.ui.com/products/253cc208-4b09-4b2e-9d1a-7aa1e8f93507/49241c96-878f-4e40-8541-c2e89c1c5e6e.png',
        'https://cdn.ecomm.ui.com/products/253cc208-4b09-4b2e-9d1a-7aa1e8f93507/9907852f-7736-4d4e-aab8-2461317dff9e.png',
        'https://cdn.ecomm.ui.com/products/253cc208-4b09-4b2e-9d1a-7aa1e8f93507/c11670af-880c-4c10-ba2b-bb62a1ce34f6.png',
    ],
    'U7-Pro-Max': [
        'https://cdn.ecomm.ui.com/products/350070a0-ae43-431b-b052-8e849c3b0a75/bad94693-bc54-4ab4-b060-9b972401941c.png',
        'https://cdn.ecomm.ui.com/products/350070a0-ae43-431b-b052-8e849c3b0a75/eadfa170-a9bf-49f2-a90c-7e6b7f2bac0d.png',
        'https://cdn.ecomm.ui.com/products/350070a0-ae43-431b-b052-8e849c3b0a75/b70e019b-8460-4691-9599-6f9853f8425f.png',
    ],
    'U7-Outdoor': [
        'https://cdn.ecomm.ui.com/products/62cc30b7-9559-480f-9668-b9edf40c0772/f2010c22-ff34-48e5-81f4-e7dad275d3c0.png',
        'https://cdn.ecomm.ui.com/products/62cc30b7-9559-480f-9668-b9edf40c0772/8d62e767-900a-4c39-bd47-91e2ff477d53.png',
        'https://cdn.ecomm.ui.com/products/62cc30b7-9559-480f-9668-b9edf40c0772/8e06ea2b-408c-4c9d-baa4-94d1f74af4d1.png',
    ],
    'U7-IW': [
        'https://cdn.ecomm.ui.com/products/9ea6158e-bc26-4ac7-946a-55eca465b059/566b1098-3514-46b6-8aee-4fde454d1205.png',
        'https://cdn.ecomm.ui.com/products/9ea6158e-bc26-4ac7-946a-55eca465b059/f5a8335d-1110-489d-aabc-5c274b3834c1.png',
        'https://cdn.ecomm.ui.com/products/9ea6158e-bc26-4ac7-946a-55eca465b059/ff60acbe-4c85-4936-a980-c9af2ad4eb2e.png',
    ],
    'U7-Mesh': [
        'https://cdn.ecomm.ui.com/products/3d6c28a3-3ba8-468c-b084-a7daf9d91d31/dd12539d-4c7e-4246-af89-b322f0ab7128.png',
        'https://cdn.ecomm.ui.com/products/3d6c28a3-3ba8-468c-b084-a7daf9d91d31/a3b68e90-93fa-4c3c-83cd-a4e8427c26a1.png',
        'https://cdn.ecomm.ui.com/products/3d6c28a3-3ba8-468c-b084-a7daf9d91d31/48348c83-fed0-4ff0-a516-9f5f177e4f6a.png',
    ],
};
// Real product photos for the client-facing device gallery, for the real
// UniFi Access reader/entry-device catalog (UNIFI_ACCESS_READER_CATALOG in
// unifiCatalog.js) — keyed by the exact `model` string stored on a reader
// device's access_model column, same pattern as WAP_MODEL_IMAGES above.
// 'UA-G3' and 'UA-G3-Flex' reuse the exact URLs already confirmed under
// ACCESS_SUBTYPE_IMAGES['reader:card']/['reader:keypad'] above (the same
// real products — G3 Reader / Reader Flex — were already photographed there
// under the generic subtype keying before per-model pricing existed); every
// other model's URLs were pulled directly from that model's own
// store.ui.com product gallery, confirmed real (cdn.ecomm.ui.com or its
// images.svc.ui.com Next.js proxy, which wraps a real cdn.ecomm.ui.com asset
// path in its own `u=` query parameter — a real, literal URL format found in
// the page, not a fabrication), 2026-07-29.
export const ACCESS_MODEL_IMAGES = {
    'UA-G3': ACCESS_SUBTYPE_IMAGES['reader:card'],
    'UA-G3-Flex': ACCESS_SUBTYPE_IMAGES['reader:keypad'],
    'UVC-G6-Pro-Entry': [
        'https://cdn.ecomm.ui.com/products/d0355a03-91c9-4d71-a5b2-003f70ef722d/8b99d956-f37d-4561-9b41-3d938e5bda42.png',
        'https://cdn.ecomm.ui.com/products/d0355a03-91c9-4d71-a5b2-003f70ef722d/7fc8ea52-f6ec-405e-9bdf-e66e1323b5fd.png',
        'https://cdn.ecomm.ui.com/products/d0355a03-91c9-4d71-a5b2-003f70ef722d/2b25b0c5-0bd9-4e28-b277-88d853dfdd16.png',
        'https://cdn.ecomm.ui.com/products/d0355a03-91c9-4d71-a5b2-003f70ef722d/46d94d06-6a35-4b25-a042-6a35676731e6.png',
    ],
    'UVC-G6-Entry': [
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2Fe0cc2520-7241-4173-9c0e-a6913eaaf629%2Fd7f7677a-8ab4-4a92-9292-bd3d18a07f27.png&q=75&w=3840',
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2Fe0cc2520-7241-4173-9c0e-a6913eaaf629%2F0a11118f-825e-4317-925f-d844b74903ea.png&q=75&w=3840',
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2Fe0cc2520-7241-4173-9c0e-a6913eaaf629%2F7003efa3-6a2b-45a2-bcfa-735bc205dc74.png&q=75&w=3840',
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2Fe0cc2520-7241-4173-9c0e-a6913eaaf629%2F94818fe3-940c-4d00-b31a-3e3a41f50ca1.png&q=75&w=3840',
    ],
    'UA-G3-Pro': [
        'https://cdn.ecomm.ui.com/products/052e61ec-7d2b-4fac-80ed-1d7aa134b409/542ff994-5b2d-49d6-aee2-9d5a972af036.png',
        'https://cdn.ecomm.ui.com/products/052e61ec-7d2b-4fac-80ed-1d7aa134b409/b9933691-d470-4e58-b3ef-11b9b799887c.png',
        'https://cdn.ecomm.ui.com/products/052e61ec-7d2b-4fac-80ed-1d7aa134b409/3a90c781-d8cd-48f5-8146-8f4d5b94e8de.png',
        'https://cdn.ecomm.ui.com/products/052e61ec-7d2b-4fac-80ed-1d7aa134b409/ee89154c-e7f8-4870-b990-180fa357417b.png',
        'https://cdn.ecomm.ui.com/products/052e61ec-7d2b-4fac-80ed-1d7aa134b409/6e58222d-d10b-480c-a957-f9f8432681d8.png',
    ],
    'UA-G2-Pro': [
        'https://cdn.ecomm.ui.com/products/39238ea9-b153-453a-82e1-c70d9d34811a/f6ee346e-ea4e-4f38-a9d6-0dcd1a2a1078.png',
        'https://cdn.ecomm.ui.com/products/39238ea9-b153-453a-82e1-c70d9d34811a/4b3db792-96e2-411b-a37a-a96ae7f64857.png',
        'https://cdn.ecomm.ui.com/products/39238ea9-b153-453a-82e1-c70d9d34811a/9ea7c371-a227-4d6e-95c8-27d16527ac4c.png',
        'https://cdn.ecomm.ui.com/products/39238ea9-b153-453a-82e1-c70d9d34811a/073d93d6-0fe5-4b70-bf8d-689caab10eb6.png',
        'https://cdn.ecomm.ui.com/products/39238ea9-b153-453a-82e1-c70d9d34811a/3a11d250-55fc-4543-a35a-a59c18d80629.png',
    ],
    'UA-Retrofit-Reader': [
        'https://cdn.ecomm.ui.com/products/bad51249-9b36-4d54-8673-90fa926fd410/652685c8-b9f8-4744-800f-f5364d044623.png',
        'https://cdn.ecomm.ui.com/products/bad51249-9b36-4d54-8673-90fa926fd410/cfd5776e-8617-4e5c-ad8a-6e64d8a0a344.png',
        'https://cdn.ecomm.ui.com/products/bad51249-9b36-4d54-8673-90fa926fd410/d9633654-7cc0-487c-abb3-bda4c6db9d54.png',
        'https://cdn.ecomm.ui.com/products/bad51249-9b36-4d54-8673-90fa926fd410/f17b018e-fb78-4957-89cb-d83f689e719f.png',
        'https://cdn.ecomm.ui.com/products/bad51249-9b36-4d54-8673-90fa926fd410/2fafa9d0-af61-455a-977e-cf12454506d4.png',
    ],
    'UA-Ultra': [
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2F8c23291f-02aa-460a-a9bb-22cdf8cd3d5f%2F8ae5a6cb-e9cd-4933-9dff-35f3806bce0e.png&q=75&w=3840',
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2F8c23291f-02aa-460a-a9bb-22cdf8cd3d5f%2F3ce33d93-a02e-4d23-a712-a07557c832b5.png&q=75&w=3840',
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2F8c23291f-02aa-460a-a9bb-22cdf8cd3d5f%2F521514b1-3a81-484b-8fe1-d3de69127a4b.png&q=75&w=3840',
        'https://images.svc.ui.com/?u=https%3A%2F%2Fcdn.ecomm.ui.com%2Fproducts%2F8c23291f-02aa-460a-a9bb-22cdf8cd3d5f%2F9db6fe02-a4df-4cc2-9e30-cdd2aed21deb.png&q=75&w=3840',
    ],
};
export const SURVEILLANCE_HEAD_END = { base: 3500, perCamera: 150 }; // NVR/VMS, licensing, storage — camera-only; a door-access system's "head end" is the panel device(s) themselves, already priced individually above.
// Flat, staff-supplied one-time labor charge for installing/configuring the
// head-end NVR/storage hardware once real rack equipment exists for a
// surveillance scope — a single fixed fee regardless of how many nvr/storage
// rack_items rows or units are involved. Distinct from SURVEILLANCE_HEAD_END
// above (the generic base+per-camera estimate used only until real rack data
// exists) and from DEVICE_RATES.camera[subtype].labor above (per-camera
// install/commissioning labor, unrelated to head-end rack work).
// **Superseded by buildlab_rate_settings for any caller that has it loaded**
// (added 2026-07-30, real staff request: "I want to have the ability to
// update the Headend install Labor price. Residential has a different
// pricing than Multifamily.") — see surveillanceHeadendInstallLabor() below.
// This constant stays as the hardcoded fallback for the brief window before
// a page's rateSettings fetch resolves, and as the value the DB row was
// itself seeded with, so nothing regresses for a caller that hasn't been
// updated to pass rateSettings through.
export const SURVEILLANCE_HEADEND_INSTALL_LABOR = 3475;
// Building-type key this app's intake form (BUILDING_TYPES, LowVoltageRequest.js)
// uses for the one case that gets its own rate — every OTHER building type
// (Multifamily, Affordable housing, Mixed-income, Senior, Mixed-use,
// Commercial, Other) shares the single "Multifamily" rate row, since none of
// them asked for their own distinct figure. Exported (2026-07-30, same day)
// because the client-facing "Self Procure" toggle (see
// scopeEquipmentHardwareTotal below) gates on this exact building type too —
// ClientProposalPage.js and ProposalSharePage.js both import it rather than
// each re-typing an em-dash literal that would silently never match if
// someone reached for a plain hyphen.
export const RESIDENTIAL_BUILDING_TYPE = 'Residential — single-family';
// Resolves the real surveillance head-end install labor rate for a request's
// building_type against the live buildlab_rate_settings rows (RateSettingsTab.js
// in the admin CRM is where staff actually edits these two numbers) — falls
// back to the flat SURVEILLANCE_HEADEND_INSTALL_LABOR constant whenever
// rateSettings hasn't loaded yet or is missing the expected key, so a caller
// never computes a $0/undefined labor figure during that brief window.
export function surveillanceHeadendInstallLabor(buildingType, rateSettings) {
    const key = buildingType === RESIDENTIAL_BUILDING_TYPE
        ? 'surveillance_headend_install_labor_residential'
        : 'surveillance_headend_install_labor_multifamily';
    const rate = rateSettings?.[key];
    return typeof rate === 'number' && !Number.isNaN(rate) ? rate : SURVEILLANCE_HEADEND_INSTALL_LABOR;
}
// Flat, staff-supplied one-time labor charge for programming the door-access
// headend (access control panel/controller configuration, hardware/software
// integration) and onboarding end users (credential enrollment, mobile
// app/fob provisioning) once real access_control_panel rack hardware exists
// for an access_control scope — same "single fixed fee regardless of row
// count/quantity" pattern as SURVEILLANCE_HEADEND_INSTALL_LABOR above, just
// for the door-access system's own headend instead of an NVR/storage one.
export const ACCESS_HEADEND_PROGRAMMING_LABOR = 5870;
// Per-device home-run cable pull (cable + pathway share + pull labor) —
// every device type EXCEPT camera (its own real, more precise
// CAMERA_CABLE_RUN rate below) and wap (already priced through the
// networking scope's Data Drops section — see estimateDeviceCost's wap
// branch comment). Set 2026-07-29 to $115 per explicit staff direction,
// matching CABLING_RATES.inUnitDrop/.microductPathway (structured_cabling's
// own real per-run rate) for one consistent cable-run figure across the
// system — was a flat $250 with no sourcing citation at all (unlike
// CAMERA_CABLE_RUN's IPVM-cited figure next to it). Applies to EVERY
// reader/panel/intercom/refuge_station/refuge_commander device regardless
// of whether it prices from a real catalog model or the legacy flat
// subtype rate — the wiring run back to the controller is the same
// physical scope of work either way, and is a distinct line from
// ACCESS_READER_INSTALL_LABOR's own door-side bundle (strike/REX/door
// contact/termination/programming), which explicitly excludes it.
export const CABLE_PER_DEVICE = 115;
// Camera cabling/materials run — originally priced separately from
// CABLE_PER_DEVICE above at $150 (an IPVM-cited Cat6 cable-run/pathway-share
// figure), lowered to $115 on 2026-07-30 per explicit staff direction to
// match the same cross-device-type CABLE_PER_DEVICE rate every other
// floor-plan device already uses — one uniform cabling/materials figure per
// device run, camera or not, rather than a camera-specific exception. Per-
// camera mount/commissioning labor lives entirely in DEVICE_RATES.
// camera[subtype].labor above (see that comment), a separate line from this
// one.
export const CAMERA_CABLE_RUN = 115;
// Unlike a WAP (mount + PoE hookup, no door hardware involved), a real
// reader install carries genuine door-side scope regardless of which
// specific reader model gets picked: electric strike, REX sensor/motion
// detector, door contact, termination, and programming/commissioning
// against the Access Hub. Reworked 2026-07-29 (was a flat $1,800 "reasoned,
// not sourced" placeholder) against real trade data, same treatment the
// camera labor formula got: NECA's Manual of Labor Units gives 0.8-1.25hr
// for a wall-mounted card reader and 1.0-1.5hr for a door contact; IPVM's
// own public forum (founder Brian Rhodes, and integrator Mike Ridgley)
// puts a full reader+strike+REX+contact install at "less than 4 hours"
// excluding cable pull, with a 4-8hr/door figure once cable IS included.
// No single canonical published standard exists for this exact bundle the
// way the camera labor reports gave one — IPVM's own dedicated "Access
// Control Labor Estimation Standards" report exists but is paywalled/
// subscriber-only — so this is a medium-confidence build-up, not a single
// sourced citation: ~3.5-6.25hr (midpoint ~5-6hr) at
// CAMERA_LABOR_RATE_PER_HOUR ($100/hr, already sourced above and reused
// here rather than inventing a second labor rate) = ~$350-625, rounded to
// $600. Deliberately EXCLUDES the wiring/conduit run back to the
// controller — every device on this branch still gets that priced via
// CABLE_PER_DEVICE below same as everything else (no exclusion — an
// earlier version of this fix incorrectly excluded modeled readers from
// CABLE_PER_DEVICE on the theory the old $1,800 figure already bundled
// wiring in; that left the wiring run priced NOWHERE once combined with
// this constant's own wiring exclusion, a real bug, corrected 2026-07-29
// per explicit staff direction: cabling is always its own $115/device
// line, full stop). Replace with BuildLab's own actual billed rate once
// known, same as the camera rate above. Used only once a real access_model
// is picked (the legacy flat DEVICE_RATES.reader subtype rates below
// already bundle an equivalent labor figure into their own all-in numbers
// and are untouched).
const ACCESS_READER_INSTALL_LABOR = 600;

// Full nouns, so an other/null subtype reads "2 cameras", not "2 camera cameras".
// Client-facing product naming per explicit staff direction (2026-07-29) —
// AI-branded names for the client-facing schedule tallies/legend, distinct
// from the plain technical short labels DeviceEditPanel.js's staff-only
// floor-plan editor dropdown still uses ('Bullet', 'Dome', '360°', etc.) —
// that one stays untouched, it's an internal editing aid, not client copy.
// doorbell stays lowercase/generic like multi_sensor/other rather than
// picking up the "Pro AI"/"AI Camera" branding above — that rename was a
// staff-directed rebrand of 5 specific named subtypes (2026-07-29), never
// requested for this one, so it isn't invented here.
const CAMERA_SUBTYPE_LABEL = { bullet: 'Bullet Pro AI', dome: 'Dome Pro AI', fisheye_360: '360 AI Camera', panoramic_180: '180 Panoramic AI', ptz: 'PTZ AI Camera', multi_sensor: 'multi-sensor camera', doorbell: 'video doorbell', other: 'camera' };
export const ACCESS_SUBTYPE_LABEL = {
    card: 'card reader', keypad: 'keypad reader', biometric: 'biometric reader', long_range: 'long-range reader', other_reader: 'reader',
    // single_door reads 'Door Controller' per explicit staff direction
    // 2026-07-30 ("single-door controller still needs to be labeled as Door
    // Controller") — the third same-day rename in this family (rack
    // category label + UA-Hub-Door product name, see unifiCatalog.js).
    // multi_door keeps its qualifier so an unmodeled multi-door panel never
    // TALLIES into the same schedule row as an unmodeled single-door one
    // (deviceSubtypeKey groups by this exact label; identical labels would
    // merge two different-priced device populations under one row).
    single_door: 'Door Controller', multi_door: 'Door Controller (multi-door)', power_supply: 'power supply', other_panel: 'controller',
    surface: 'surface-mount intercom', recessed: 'recessed intercom', pedestal: 'pedestal intercom', gooseneck: 'gooseneck intercom', other_intercom: 'intercom',
    // Title-cased (unlike the lowercase full-noun phrases above) since these
    // two double as the subtype-picker/inspector display text elsewhere —
    // still reads fine inlined into a device-schedule sentence.
    standard: 'Call Station - Standard (indoor)', weatherproof: 'Weatherproof (outdoor)', other_refuge_station: 'call station',
    commander: 'Call Commander console',
};
// access_subtype strings are shared across reader/panel/intercom/refuge_station
// families with no collisions (see the DB CHECK constraint), so a device's own
// device_type is only needed to resolve a null/legacy subtype's fallback label.
const accessDeviceLabel = (device) => {
    if (device.device_type === 'reader' && device.access_model) {
        const entry = UNIFI_ACCESS_READER_CATALOG.find((c) => c.model === device.access_model);
        if (entry) return `${entry.product} reader`;
    }
    if (device.device_type === 'intercom' && device.access_model) {
        const entry = UNIFI_ACCESS_READER_CATALOG.find((c) => c.model === device.access_model && c.hasIntercom);
        if (entry) return `${entry.product} intercom`;
    }
    // A modeled panel gets its OWN tally row too (added 2026-07-30 with the
    // panel model picker — staff report: "I noticed in the access control
    // that 1 Single Door Control without manufacturer and model is 1500") —
    // same invariant as the reader/intercom/camera branches above:
    // EquipmentDeviceSchedule.js's deviceManufacturerModel() assumes every
    // device sharing one tally row shares one real model, which only holds
    // if the tally key itself encodes the model. No " controller" suffix —
    // these product names ("Door Controller" — UA-Hub-Door's staff-directed
    // display name, see its unifiCatalog.js entry — "UniFi Access
    // Enterprise Access Hub", "UniFi Access Retrofit PSU 12V") already read
    // as head-end hardware on their own, unlike the reader line's shorter
    // names ("Access Ultra") that needed the noun appended.
    if (device.device_type === 'panel' && device.access_model) {
        const entry = truePanelControllers().find((c) => c.model === device.access_model);
        if (entry) return entry.product;
    }
    return ACCESS_SUBTYPE_LABEL[device.access_subtype] || ACCESS_SUBTYPE_LABEL[`other_${device.device_type}`] || device.device_type;
};
const wapDeviceLabel = (device) => (device.wap_model ? `${device.wap_model} access point` : 'wireless access point');
// Same "model overrides the generic subtype bucket" convention as
// wapDeviceLabel above — a camera with a real camera_model gets its OWN
// tally row (distinct brand/product name) instead of blending into the
// generic subtype bucket (e.g. "Bullet Pro AI"), which matters once
// multiple brands/models can share one camera_subtype: two different real
// bullet cameras must never silently tally as one row claiming a single
// manufacturer/model in EquipmentDeviceSchedule.js's deviceManufacturerModel()
// (that function's own "any one representative device carries the real
// answer for the whole row" invariant only holds if every device sharing a
// tally key genuinely shares the identical model too).
const cameraDeviceLabel = (device) => {
    const entry = findCameraCatalogEntry(device.camera_model);
    return entry ? `${entry.brand} ${entry.product}` : (CAMERA_SUBTYPE_LABEL[device.camera_subtype] || 'camera');
};

// True when a reader/intercom/panel device has a real catalog model picked —
// shared by estimateDeviceCost's reader/intercom/panel pricing branches
// below, so they can never drift out of sync on what counts as "a real
// model" (an intercom only counts when its model is also hasIntercom — a
// video door station's own product line — never a plain reader-only model;
// a panel only counts against truePanelControllers(), the
// access_control_panel head-end catalog, never a reader SKU — the two
// populations share the one access_model column but are disjoint product
// lines). Extended to panels 2026-07-30 alongside DEVICE_RATES.panel's
// hardware/labor split — same single-source-of-truth convention, one
// helper deciding "real model or not" for every device family that has a
// model picker.
const hasRealAccessCatalogModel = (d) => {
    if (!d.access_model) return false;
    if (d.device_type === 'reader') return UNIFI_ACCESS_READER_CATALOG.some((c) => c.model === d.access_model);
    if (d.device_type === 'intercom') return UNIFI_ACCESS_READER_CATALOG.some((c) => c.model === d.access_model && c.hasIntercom);
    if (d.device_type === 'panel') return truePanelControllers().some((c) => c.model === d.access_model);
    return false;
};

// Prices ONE device — {hardware, labor, cabling, tallyKey}. Factored out of
// estimateDeviceCost() (2026-07-30) so a single device's real installed cost
// can be computed identically in exactly one place: the scope-level total
// below AND EquipmentDeviceSchedule.js's per-row Price column both call this,
// so they can never drift out of sync the way two independently-maintained
// copies of this branching logic eventually would. Every branch here,
// including its reasoning comments, is unchanged from before this
// extraction — only the shape changed (returns a value instead of mutating
// outer `hardware`/`labor` accumulators).
function deviceLineCost(d, markupPct = null) {
    const base = deviceLineCostComputed(d, markupPct);
    // A real staff-entered per-unit price (added 2026-07-30) REPLACES only
    // the HARDWARE component for this device — labor and cabling still come
    // from the normal per-subtype formula regardless. Originally this
    // replaced hardware+labor+cabling entirely, on the theory that a real
    // staff-entered number was the complete installed cost — but real usage
    // (7175 Sitio Caballero, 2026-07-30) showed staff enter a real per-unit
    // HARDWARE price (what the camera itself costs) expecting install labor
    // and the cable run to still be tracked separately, the same way a real
    // camera_model pick already works (see deviceLineCostComputed's camera
    // branch: a modeled camera's real hardware price ALSO doesn't zero out
    // labor). Zeroing labor/cabling on override silently erased ~$1,435 of
    // real camera install labor and $1,050 of cabling from that scope's
    // total — a real, reported pricing bug, not the intended behavior.
    // The override is the RAW pre-markup hardware cost — the scope's markup
    // (equipment_markup_pct, else the flat 20%) applies ON TOP of it, same
    // as a rack row's unit_cost. For a few hours on 2026-07-30 this was
    // built the other way ("a staff-entered price is final, no markup"),
    // reversed the SAME day by live staff feedback on 7175's Equipment
    // Schedule, where all 7 cameras carry overrides (154.99/143.99/318.99/
    // 186.99): "these should all be updating when i change the markup
    // percentage manually" — an overridden row sitting frozen while every
    // other row moved with the markup control read as broken, and staff's
    // mental model is override = real vendor cost, markup = quoting policy,
    // exactly the rack_items convention.
    // tallyKey/isCamera stay from the normal computation so an overridden
    // device still tallies into the SAME Equipment Schedule row as its
    // unmodified siblings, rather than splitting into a separate bucket.
    if (d.price_override != null && d.price_override !== '' && !Number.isNaN(Number(d.price_override))) {
        return { ...base, hardware: Number(d.price_override) * markupMultiplier(markupPct) };
    }
    return base;
}

function deviceLineCostComputed(d, markupPct = null) {
    const rates = DEVICE_RATES[d.device_type];
    // Scope-level markup override (see markupMultiplier above) — replaces
    // the flat HARDWARE_MARKUP in every branch below that marks up a clean
    // per-item hardware figure. Falls back to the global 20% when the scope
    // has no override set (markupPct null/absent), so every branch's price
    // is byte-identical to before this parameter existed in that case. The
    // legacy "all-in" DEVICE_RATES branches at the bottom never applied a
    // markup at all and still don't — a custom pct must not start inflating
    // figures the flat 20% deliberately left alone.
    const mult = markupMultiplier(markupPct);
    if (d.device_type === 'camera') {
        // hasOwnProperty guard: a subtype string matching an inherited
        // Object key ('toString', …) must fall back to 'other', not
        // concatenate a function into the total.
        const sub = Object.prototype.hasOwnProperty.call(rates, d.camera_subtype) ? d.camera_subtype : 'other';
        // Real per-model hardware price from CAMERA_CATALOG (cameraCatalog.js)
        // when camera_model is set, same "real model overrides the generic
        // subtype placeholder" precedent as wap_model/access_model below —
        // a specific camera's real cost varies by brand/tier ($99-$400+
        // across the Reolink/UniFi lines alone) far more than one flat
        // per-subtype number can defensibly cover once a real model is
        // picked. Labor is DELIBERATELY still `rates[sub].labor` even with
        // a real model set — mounting/aim-and-focus labor is a function of
        // the camera's physical FORM (bullet vs. dome vs. ptz vs. doorbell),
        // not its brand, so a Reolink PTZ and a UniFi PTZ take the same
        // real install time despite different hardware cost.
        const catalogEntry = findCameraCatalogEntry(d.camera_model);
        return {
            tallyKey: catalogEntry ? catalogEntry.model : sub,
            hardware: (catalogEntry ? catalogEntry.priceUsd : rates[sub].hardware) * mult,
            labor: rates[sub].labor,
            cabling: CAMERA_CABLE_RUN,
            isCamera: true,
        };
    }
    if (d.device_type === 'wap') {
        // Real per-model hardware price from the catalog, marked up —
        // an AP's real cost varies too widely by model ($79–$1,999) for a
        // single placeholder to be defensible once a model is picked.
        // Deliberately NO labor and NO cabling contribution here (unlike
        // every other device type below) — staff confirmed 2026-07-29
        // that for the networking scope, install labor for a WAP's data
        // drop is already priced through the Data Drops — Operations &
        // Common Areas section (commonAreaDropsCost()), and the
        // networking scope's head-end (switches/router) is priced
        // through its own Rack Builder — see AdminProposalPage.js's
        // suggestion calc, which excludes 'networking' from folding
        // rackBuilderSummary() into this device-cost estimate for the
        // same reason. Folding either in here would double-count real
        // cost the client would be charged for twice.
        const catalogEntry = d.wap_model ? UNIFI_CATALOG.find((c) => c.category === 'wap_controller' && c.model === d.wap_model) : null;
        return {
            tallyKey: catalogEntry ? catalogEntry.model : 'other',
            hardware: (catalogEntry ? catalogEntry.priceUsd : DEVICE_RATES.wap.other) * mult,
            labor: 0,
            cabling: 0,
        };
    }
    if (d.device_type === 'reader' && hasRealAccessCatalogModel(d)) {
        // Real per-model hardware price from the catalog + a flat
        // door-side install labor figure (strike/REX/wiring/programming
        // — see ACCESS_READER_INSTALL_LABOR's own comment), rather than
        // the legacy flat DEVICE_RATES.reader subtype rate — a reader's
        // real hardware cost varies too widely by model ($129-$379,
        // several with a built-in camera) for a single subtype
        // placeholder to be defensible once a real model is picked.
        // Falls through to the generic access_subtype branch below when
        // access_model isn't set — fully backward compatible.
        const catalogEntry = UNIFI_ACCESS_READER_CATALOG.find((c) => c.model === d.access_model);
        return {
            tallyKey: catalogEntry.model,
            hardware: catalogEntry.priceUsd * mult,
            labor: ACCESS_READER_INSTALL_LABOR,
            cabling: CABLE_PER_DEVICE,
        };
    }
    if (d.device_type === 'intercom' && hasRealAccessCatalogModel(d)) {
        // A UniFi video door station (G6/G3 Pro/G2 Pro line) is
        // simultaneously an intercom AND a reader — same catalog,
        // filtered to hasIntercom (see trueAccessIntercoms() in
        // unifiCatalog.js) — so it prices exactly like a modeled reader
        // above: real per-model hardware + the same door-side install
        // labor figure (ACCESS_READER_INSTALL_LABOR's own comment now
        // covers both). Falls through to the legacy flat
        // DEVICE_RATES.intercom subtype rate below when access_model
        // isn't set — fully backward compatible.
        const catalogEntry = UNIFI_ACCESS_READER_CATALOG.find((c) => c.model === d.access_model && c.hasIntercom);
        return {
            tallyKey: catalogEntry.model,
            hardware: catalogEntry.priceUsd * mult,
            labor: ACCESS_READER_INSTALL_LABOR,
            cabling: CABLE_PER_DEVICE,
        };
    }
    if (d.device_type === 'panel' && hasRealAccessCatalogModel(d)) {
        // Real per-model hardware price for a controller/power-enclosure
        // device (added 2026-07-30, real staff report: "I noticed in the
        // access control that 1 Single Door Control without manufacturer
        // and model is 1500" — approved fix: give panels the same real
        // model picker readers/intercoms already have). Hardware comes from
        // truePanelControllers()'s real catalog price (UA-Hub-Door $199 /
        // EAH-8 $999 / UACC-Retrofit-PSU-12V $79), marked up like every
        // other clean per-item hardware figure. Labor is the panel
        // SUBTYPE's own labor component from DEVICE_RATES.panel — NOT
        // ACCESS_READER_INSTALL_LABOR, which is door-side READER work
        // (strike/REX/door contact/termination at the controlled opening
        // itself); a controller's install is head-end-side work (enclosure/
        // DIN-rail mount, power, network uplink, termination, programming).
        // Since the same-day policy correction on DEVICE_RATES.panel ("The
        // 5870 already includes the labor for all Door Controllers which
        // includes the single hub") every panel subtype's labor is 0 — all
        // controller labor bills once via ACCESS_HEADEND_PROGRAMMING_LABOR —
        // so this branch contributes hardware only; the rates[sub].labor
        // read is kept so any future re-split flows through unchanged.
        const catalogEntry = truePanelControllers().find((c) => c.model === d.access_model);
        const sub = Object.prototype.hasOwnProperty.call(rates, d.access_subtype) ? d.access_subtype : 'other';
        return {
            tallyKey: catalogEntry.model,
            hardware: catalogEntry.priceUsd * mult,
            labor: rates[sub].labor,
            cabling: CABLE_PER_DEVICE,
        };
    }
    if (d.device_type === 'panel') {
        // Unmodeled panel — prices from DEVICE_RATES.panel's {hardware,
        // labor} split (2026-07-30; labor zeroed the same day per the
        // staff policy on that entry's own comment — controller labor
        // bills once via ACCESS_HEADEND_PROGRAMMING_LABOR, so this is
        // hardware-only in practice). This branch MUST run before the
        // generic flat-rate branch below — panel's rates are now objects,
        // and the generic branch's `hardware: rates[sub]` would concatenate
        // an object into the total.
        const sub = Object.prototype.hasOwnProperty.call(rates, d.access_subtype) ? d.access_subtype : 'other';
        const entry = rates[sub];
        return {
            tallyKey: sub,
            hardware: entry.hardware * mult,
            labor: entry.labor,
            cabling: CABLE_PER_DEVICE,
        };
    }
    if ((d.device_type === 'refuge_station' || d.device_type === 'refuge_commander') && rates && typeof rates === 'object') {
        // refuge_station/refuge_commander carry a real {hardware, labor}
        // split (see DEVICE_RATES above — fixed 2026-07-29) rather than
        // one flat "all-in" number, so HARDWARE_MARKUP applies to the
        // real hardware/signage portion like every other clean per-item
        // hardware figure, and labor now actually reaches the `labor`
        // accumulator instead of silently vanishing into `hardware` via
        // the generic branch below — a confirmed user-reported bug (the
        // Running Total's Labor line showed nothing for an Area of
        // Refuge floor plan even though $552-$850/station of real labor
        // was baked into the old flat rate).
        const sub = Object.prototype.hasOwnProperty.call(rates, d.access_subtype) ? d.access_subtype : 'other';
        const entry = rates[sub];
        return {
            tallyKey: sub,
            hardware: entry.hardware * mult,
            labor: entry.labor,
            cabling: CABLE_PER_DEVICE,
        };
    }
    if (rates && typeof rates === 'object') {
        // No HARDWARE_MARKUP here on purpose — a reader/intercom with no
        // specific hardware model prices from this legacy flat
        // DEVICE_RATES subtype rate, which is a documented "all-in"
        // figure bundling strike/REX wiring or similar labor into one
        // number with no sourced hardware/labor split. Marking it up
        // would silently inflate its embedded labor too. (panel left this
        // branch 2026-07-30 — its rates are now {hardware, labor} objects
        // handled by the dedicated panel branch above.)
        const sub = Object.prototype.hasOwnProperty.call(rates, d.access_subtype) ? d.access_subtype : 'other';
        return { tallyKey: sub, hardware: rates[sub], labor: 0, cabling: CABLE_PER_DEVICE };
    }
    // Same reasoning — DEVICE_RATES.other is an undocumented legacy
    // catch-all with no confirmed hardware/labor split, left unmarked.
    return { tallyKey: 'other', hardware: DEVICE_RATES.other, labor: 0, cabling: CABLE_PER_DEVICE };
}

// Real per-device installed cost (hardware + labor + cabling, HARDWARE_MARKUP
// already applied where it applies) — exported so EquipmentDeviceSchedule.js
// can price a schedule row (qty × this) using the EXACT same math
// estimateDeviceCost() sums across a whole scope, never a second,
// independently-derived number for the same device. markupPct is the scope's
// own equipment_markup_pct override (see markupMultiplier) — pass the same
// value here and to estimateDeviceCost() or the schedule row and the
// Running Total will disagree for the same device, the exact drift this
// shared function exists to prevent.
export function deviceInstalledCost(d, markupPct = null) {
    const c = deviceLineCost(d, markupPct);
    return { hardware: c.hardware, labor: c.labor, cabling: c.cabling, total: c.hardware + c.labor + c.cabling };
}

// Price a scope from placed devices (surveillance or door-access). Returns
// null when there are no devices to price from (callers fall back to the
// per-unit model).
export function estimateDeviceCost(devices, markupPct = null) {
    if (!devices || !devices.length) return null;
    const bySubtype = {};
    let hardware = 0, labor = 0, cabling = 0, cameraCount = 0;
    devices.forEach((d) => {
        const c = deviceLineCost(d, markupPct);
        const typeKey = d.device_type === 'camera' ? 'camera' : d.device_type;
        (bySubtype[typeKey] = bySubtype[typeKey] || {})[c.tallyKey] = (bySubtype[typeKey]?.[c.tallyKey] || 0) + 1;
        hardware += c.hardware;
        labor += c.labor;
        // wap's cable-run cost is already priced through the networking
        // scope's Data Drops section (see deviceLineCost's wap branch) —
        // its own cabling is always 0, so no exclusion needed here.
        cabling += c.cabling;
        if (c.isCamera) cameraCount += 1;
    });
    // This 20%-of-equipment figure is a LABOR estimate (install/commissioning
    // time for the head-end), a completely different "20%" from
    // HARDWARE_MARKUP above — computed off the RAW (pre-markup) equipment
    // estimate so this labor number stays exactly what it was before
    // HARDWARE_MARKUP existed, per explicit direction that labor stays
    // untouched. `headEnd` itself (the pure equipment/licensing figure, its
    // own visible line item) gets HARDWARE_MARKUP applied separately below.
    const headEndRaw = cameraCount > 0 ? SURVEILLANCE_HEAD_END.base + SURVEILLANCE_HEAD_END.perCamera * cameraCount : 0;
    // Kept SEPARATE from the shared `labor` accumulator (camera/WAP/cabling
    // install labor) — a caller with REAL rack-priced head-end data (see
    // AdminProposalPage.js's suggestion calc) discards this generic head-end
    // estimate entirely in favor of real hardware + a real flat install-labor
    // charge, and must be able to discard its labor portion too, not just its
    // hardware portion. Folding this into `labor` unconditionally made that
    // impossible and silently double-counted a phantom "20% of a guess nobody
    // is using" charge on top of the real head-end whenever a real rack
    // override applied — a confirmed bug, fixed here.
    const headEndLaborEstimate = headEndRaw * 0.2;
    // Same scope-level override as the per-device hardware above — the
    // generic head-end estimate is an equipment figure, so a scope's custom
    // equipment markup governs it too; labor (headEndLaborEstimate) stays
    // computed off the RAW figure regardless, same as under the flat 20%.
    const headEnd = headEndRaw * markupMultiplier(markupPct);
    // Cabling (cameras' own real, more precise CAMERA_CABLE_RUN rate;
    // CABLE_PER_DEVICE for reader/panel/intercom/refuge_station/
    // refuge_commander; $0 for wap, already priced through the networking
    // scope's Data Drops section) is now accumulated in the same single pass
    // as hardware/labor above, inside deviceLineCost() — see that function's
    // per-branch comments for the full reasoning (previously a second,
    // separate forEach over `devices` computed this; folded in 2026-07-30
    // when deviceLineCost() was extracted, since nothing here depends on
    // hardware/labor/cameraCount having already been fully summed first).
    const total = hardware + labor + headEndLaborEstimate + cabling + headEnd;
    return { deviceCount: devices.length, cameraCount, bySubtype, hardware, labor, headEndLaborEstimate, cabling, headEnd, total };
}

// Shared Building/Floor label convention — building_label/floor_label are
// stored as bare identifiers ("1"), always spelled out as "Building 1"/
// "Floor 1", with the Building segment shown only when a set of floorplans
// genuinely spans more than one building (same "don't show Building noise
// on a single-building proposal" fix SurveillanceFloorplanViewer.js already
// made once for its own tabs — this was a second, independent copy of that
// same label logic that never got the memo, joining raw label values with a
// bare ' · ' and no "Building"/"Floor" words at all, e.g. "1 · 1"). Now the
// one source of truth; SurveillanceFloorplanViewer.js imports these instead
// of keeping its own local copies.
export const buildingText = (f) => (f.building_label ? `Building ${f.building_label}` : null);
export const floorText = (f, i) => (f.floor_label ? `Floor ${f.floor_label}` : `Floor ${i + 1}`);
export const hasMultipleBuildings = (floorplans) =>
    new Set((floorplans || []).map((f) => f.building_label).filter(Boolean)).size > 1;
export function floorplanLabel(f, i, floorplans) {
    return hasMultipleBuildings(floorplans) ? [buildingText(f), floorText(f, i)].filter(Boolean).join(' : ') : floorText(f, i);
}

// Per-subtype device tally for one floor's devices — the counting logic
// deviceScheduleText needs for its own text, extracted so
// SurveillanceFloorplanViewer's legend can show the same subtype-level
// counts ("3 U7-Pro access points, 1 U7-LR access point") the interactive
// map itself, not just the text schedule — the one piece of information the
// map's device-type-only legend didn't already carry.
// Single-device version of deviceSubtypeCounts' own key logic — exported so
// a caller that needs to know WHICH device(s) landed in a tally group (e.g.
// EquipmentDeviceSchedule.js pulling a real manufacturer/model for the row)
// can re-derive the exact same grouping key per device, instead of only
// getting back pre-aggregated counts.
export function deviceSubtypeKey(d) {
    return d.device_type === 'camera'
        ? cameraDeviceLabel(d)
        : d.device_type === 'wap'
        ? wapDeviceLabel(d)
        : accessDeviceLabel(d);
}
export function deviceSubtypeCounts(devices) {
    const counts = {};
    (devices || []).forEach((d) => {
        const key = deviceSubtypeKey(d);
        counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([key, count]) => ({ key, count }));
}

// Human-readable device schedule from a set of floorplans (each with a
// .devices array) — for the live EquipmentDeviceSchedule card (never a
// pasted snapshot — see that component's own header comment for why).
export function deviceScheduleText(floorplans) {
    const lines = [];
    let totalDevices = 0;
    floorplans.forEach((fp, i) => {
        const devices = fp.devices || [];
        if (!devices.length) return;
        totalDevices += devices.length;
        const counts = deviceSubtypeCounts(devices);
        const floorName = floorplanLabel(fp, i, floorplans);
        const parts = counts.map(({ key, count }) => `${count} ${key}${count > 1 ? 's' : ''}`);
        lines.push(`• ${floorName}: ${parts.join(', ')}`);
    });
    if (!lines.length) return '';
    return `Device schedule (${totalDevices} devices):\n${lines.join('\n')}`;
}

// ---------------------------------------------------------------------------
// Surveillance storage/retention calculator — camera count is ALWAYS the real
// count from placed devices (estimateDeviceCost's cameraCount off the actual
// floor plan), never a manually-typed number, so this recalculates the moment
// staff adds/removes a camera in the floor plan editor and reopens the scope.
// Bitrate-per-resolution and codec-efficiency figures are standard published
// industry planning numbers (Axis/Milestone/Genetec-style design-tool
// figures), not vendor-specific; drive $/TB is a real current street price
// for a surveillance-rated HDD (Seagate SkyHawk / WD Purple), not invented.
export const STORAGE_RESOLUTIONS = [
    { key: '1mp', label: '1MP (720p)', mbps: 2 },
    { key: '2mp', label: '2MP (1080p)', mbps: 4 },
    { key: '4mp', label: '4MP', mbps: 8 },
    { key: '4k', label: '4K (8MP)', mbps: 16 },
];
export const STORAGE_CODECS = [
    { key: 'h264', label: 'H.264', mult: 1 },
    { key: 'h265', label: 'H.265', mult: 0.5 },
    { key: 'h265+', label: 'H.265+ (smart)', mult: 0.3 },
];
export const STORAGE_FPS_OPTIONS = [10, 15, 20, 30];
export const STORAGE_RECORDING_MODES = [
    { key: 'continuous', label: 'Continuous', mult: 1 },
    // ~30% of continuous is a standard planning assumption for motion/
    // activity-triggered recording — real usage varies by site, not a precise
    // figure, which is why this stays an estimator input, not a hidden constant.
    { key: 'motion', label: 'Motion-triggered', mult: 0.3 },
];
const STORAGE_DRIVE_SIZES_TB = [4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
export const SURVEILLANCE_DRIVE_RATE_PER_TB = 20; // real ~$17-25/TB street price, surveillance-rated HDD, rounded

export function surveillanceStorageEstimate({
    cameraCount = 0,
    resolution = '4mp',
    codec = 'h265',
    fps = 30,
    recordingMode = 'continuous',
    retentionDays = 30,
} = {}) {
    const n = Number(cameraCount) || 0;
    const res = STORAGE_RESOLUTIONS.find((r) => r.key === resolution) || STORAGE_RESOLUTIONS[2];
    const cd = STORAGE_CODECS.find((c) => c.key === codec) || STORAGE_CODECS[1];
    const mode = STORAGE_RECORDING_MODES.find((m) => m.key === recordingMode) || STORAGE_RECORDING_MODES[0];
    // fps scaling is a simple linear approximation relative to the 30fps
    // baseline bitrate — real encoders don't scale perfectly linearly, but
    // for a planning-level estimator this is the standard simplification.
    const bitrateMbpsPerCamera = res.mbps * cd.mult * (Number(fps) / 30) * mode.mult;
    const totalMbps = bitrateMbpsPerCamera * n;
    // GB/day = Mbps × 1,000,000 bits/sec × 86,400 sec/day / 8 bits/byte / 1e9 bytes/GB
    const gbPerDay = totalMbps * 86400 / 8000;
    const tbNeeded = (gbPerDay * (Number(retentionDays) || 0)) / 1000;
    const driveTb = STORAGE_DRIVE_SIZES_TB.find((d) => d >= tbNeeded) || (tbNeeded > 0 ? Math.ceil(tbNeeded / 2) * 2 : 0);
    const price = Math.round((driveTb * SURVEILLANCE_DRIVE_RATE_PER_TB * HARDWARE_MARKUP) / 50) * 50;
    const text = n > 0
        ? `Storage estimate: ${n} camera${n === 1 ? '' : 's'} @ ${res.label}, ${cd.label}, ${fps}fps, ${mode.label.toLowerCase()} recording ≈ ${bitrateMbpsPerCamera.toFixed(1)} Mbps/camera (${gbPerDay.toFixed(0)} GB/day total). ${retentionDays}-day retention needs ≈ ${tbNeeded.toFixed(1)}TB raw — a ${driveTb}TB surveillance-rated drive covers it (no RAID/redundancy overhead included). Verify current drive pricing before quoting.`
        : 'No cameras placed on this scope\'s floor plan yet — add cameras in the floor plan editor to estimate storage.';
    return { cameraCount: n, bitrateMbpsPerCamera, gbPerDay, tbNeeded, driveTb, price, text };
}

// ---------------------------------------------------------------------------
// Structured-cabling takeoff: per-element planning rates and a calculator
// that turns the real answers (unit mix by bedroom count, drops per room,
// homeruns, coax) into exact quantities, an itemized scope of work, and a
// computed price. Rates are installed (materials + labor), tuned so typical
// configs land inside the same $350–650/unit planning band as the per-unit
// SCOPES model. Deliberately scoped to in-unit/unit-to-SMC cabling only —
// IDF closet build-out (racks, patch panels), the MPOE→IDF fiber backbone,
// and common-area (leasing/amenity/mech) drops are all Data/Communications'
// concern now (that scope owns IDF/MDF design end-to-end via its own Rack
// Builder), not this takeoff's — see the WAP/networking scope's rack_items.
export const CABLING_RATES = {
    inUnitDrop: 115,       // Cat6, SMC → room outlet
    // IDF → unit is fiber MICRODUCT PATHWAY only — empty conduit + pull
    // string installed at rough-in, sized for future fiber. Deliberately
    // does NOT include fiber cable, blowing, termination, or the ONT/media
    // converter at the SMC end — those are activation-phase work, by the
    // ISP or a separate networking scope, not construction-phase cabling.
    // That's why this rate is lower than a terminated/certified Cat6 pull.
    microductPathway: 115,
    // Flat installed rate for the in-unit structured media center panel —
    // mounting labor + a basic/unspecified enclosure. NOT tied to any real
    // hardware size or model; this is what's baked into every base takeoff
    // total regardless of which panel actually ends up installed. When staff
    // knows the ISP does NOT supply the physical enclosure (or a specific
    // size is required), price the real hardware as an "Add Alternate" via
    // SMC_PANEL_OPTIONS/smcPanelAlternate() below instead of editing this.
    smcEnclosure: 95,
    coaxDrop: 55,          // RG6, SMC → outlet
    mpoeBuildout: 3200,    // MPOE room build-out & demarc extension
    // RG6 quad-shield coax BACKBONE home run, IDF → unit SMC — like fiberPerIdf
    // (not microductPathway), this is real terminated cable, not empty conduit:
    // cable, connectors, and termination at both ends, ready to activate. Priced
    // per unit (one home run per unit), same shape as pathwaysPerUnit below.
    // Not part of the base takeoff — see coaxHomerunAlternate() for pricing this
    // as a staff-authored "Add Alternate" line on a scope, not baked into cost.
    coaxHomerun: 165,
};

const MIX_LABELS = { studio: 'studio', br1: '1BR', br2: '2BR', br3: '3BR', br4: '4BR', br5: '5BR' };
const MIX_BEDROOMS = { studio: 0, br1: 1, br2: 2, br3: 3, br4: 4, br5: 5 };

export function cablingTakeoff({
    mix = { studio: 0, br1: 0, br2: 0, br3: 0, br4: 0, br5: 0 },
    dropsPerBedroom = 1,
    livingDrops = 1,
    coax = 'living', // 'none' | 'living' | 'all'
    pathwaysPerUnit = 1, // fiber microduct pathways, IDF → unit SMC (empty conduit — see CABLING_RATES.microductPathway)
} = {}) {
    const units = Object.values(mix).reduce((a, n) => a + (Number(n) || 0), 0);
    const bedrooms = Object.entries(mix).reduce((a, [k, n]) => a + MIX_BEDROOMS[k] * (Number(n) || 0), 0);
    // Studios get the living-room drops only — no separate bedroom.
    const inUnitDrops = bedrooms * dropsPerBedroom + units * livingDrops;
    const coaxDrops = coax === 'none' ? 0 : coax === 'living' ? units : units + bedrooms;
    const pathways = units * pathwaysPerUnit;

    const cost = {
        inUnit: inUnitDrops * CABLING_RATES.inUnitDrop,
        pathways: pathways * CABLING_RATES.microductPathway,
        smc: units * CABLING_RATES.smcEnclosure,
        coax: coaxDrops * CABLING_RATES.coaxDrop,
        mpoe: CABLING_RATES.mpoeBuildout,
    };
    const total = Object.values(cost).reduce((a, n) => a + n, 0);

    const mixText = Object.entries(mix)
        .filter(([, n]) => Number(n) > 0)
        .map(([k, n]) => `${n}×${MIX_LABELS[k]}`)
        .join(', ');
    const lines = [
        `Scope of work — structured cabling takeoff (${units} units${mixText ? `: ${mixText}` : ''}):`,
        `• Distribution — ${pathwaysPerUnit}× fiber microduct pathway per unit, IDF → in-unit SMC enclosure (${pathways} pathways, ${units} SMC panels). Empty conduit only — fiber cable, blow-in, termination, and the ONT/media converter at the SMC are NOT included in this scope; coordinate with the ISP or networking scope for activation.`,
        `• In-unit — ${dropsPerBedroom}× Cat6 per bedroom + ${livingDrops}× to living room, fed from the SMC media converter (${inUnitDrops} drops)${coaxDrops ? `; RG6 coax ${coax === 'living' ? 'to living rooms' : 'to living rooms + bedrooms'} (${coaxDrops} coax)` : ''}`,
        '• MPOE build-out — demarc extension, grounding & bonding',
        '• Termination, labeling, and test & certification of every in-unit copper link; as-built documentation',
    ];

    return {
        counts: { units, bedrooms, inUnitDrops, coaxDrops, pathways },
        cost,
        total: Math.round(total / 100) * 100,
        text: lines.join('\n'),
    };
}

// A construction-style "Add Alternate": a priced add-on scope of work a
// client can consider on top of a scope's base price, kept as its own line
// (see buildlab_proposal_scopes.alternates) rather than folded into the base
// total — same bid pattern as a numbered Add Alternate on a construction bid
// form. This one prices a coax backbone home run (IDF → unit SMC) as an
// addition alongside the base scope's fiber microduct pathway, for projects
// where coax is wanted as a redundant/alternate path rather than the default.
// Per-unit-type drop breakdown, e.g. for the Proposal Document's "Scope of
// work" detail — same in-unit-drop formula cablingTakeoff() applies in
// aggregate (bedrooms×dropsPerBedroom + units×livingDrops), just kept split
// out by bedroom-count type instead of summed across the whole mix. Reuses
// MIX_LABELS/MIX_BEDROOMS rather than re-declaring the type list, so this
// can't silently drift from cablingTakeoff()'s own math.
const MIX_ORDER = ['studio', 'br1', 'br2', 'br3', 'br4', 'br5'];
export function unitMixDropBreakdown(mix = {}, { dropsPerBedroom = 1, livingDrops = 1 } = {}) {
    const rows = MIX_ORDER.map((k) => {
        const units = Number(mix[k]) || 0;
        if (!units) return null;
        const bedroomsPerUnit = MIX_BEDROOMS[k];
        const dropsPerUnit = bedroomsPerUnit * dropsPerBedroom + livingDrops;
        return { key: k, label: MIX_LABELS[k], units, bedroomsPerUnit, dropsPerUnit, totalDrops: units * dropsPerUnit };
    }).filter(Boolean);
    const totals = rows.reduce((a, r) => ({ units: a.units + r.units, totalDrops: a.totalDrops + r.totalDrops }), { units: 0, totalDrops: 0 });
    return { rows, totals };
}

// Data/Communications' "Data Drops — Operations & Common Areas" list —
// staff-authored rows ({location, qty, notes}) for spaces that need a real
// Cat6 drop but aren't a resident unit and aren't a WAP: leasing office,
// community room, mail room, business center, etc. Priced at
// CABLING_RATES.inUnitDrop — the same real installed Cat6-drop cost as an
// in-unit drop (SMC/IDF → outlet, materials + labor); a common-area drop is
// the identical physical scope of work, so this reuses that rate directly
// rather than inventing a second one. Distinct from cablingTakeoff()'s own
// per-unit math (structured_cabling's concern) and from CommonAreaExamples
// (staff-uploaded example marketing photos, not real per-project counts).
export function commonAreaDropsCost(drops) {
    const rows = Array.isArray(drops) ? drops : [];
    const totalDrops = rows.reduce((a, d) => a + (Number(d.qty) || 0), 0);
    return { totalDrops, total: totalDrops * CABLING_RATES.inUnitDrop };
}

// FFE - A/V and Computers scope's per-location equipment list — staff-
// authored rows ({location, description, price, qty}), one per location's
// proposed computer/AV hardware, e.g. {location: 'PM Office', description:
// 'Lenovo AIO Computer Ryzen AI 5, 16GB DDR, 1TB SSD, Windows 11', price:
// 1299.99}. price is the real vendor cost — HARDWARE_MARKUP applies here
// exactly as it does to every other clean per-item hardware figure, not
// baked into the stored price. qty defaults to 1 (most rows are one
// specific machine at one specific location; a location wanting several of
// the same spec sets qty instead of duplicating the row). Clients toggle
// each row on/off client-side to build their own equipment list (ephemeral
// selection state, same pattern as design_management's media add-ons) — see
// ClientProposalPage.js/ProposalSharePage.js. ffeItemCost is exported
// separately so both the admin suggestion total AND each client-facing
// toggle row can price a single item identically, never drifting apart.
export function ffeItemCost(item) {
    return (Number(item?.price) || 0) * (Number(item?.qty) || 1) * HARDWARE_MARKUP;
}
export function ffeItemsCost(items) {
    const rows = Array.isArray(items) ? items : [];
    const total = rows.reduce((a, r) => a + ffeItemCost(r), 0);
    return { count: rows.length, total: Math.round(total) };
}

// Groups ffe_items by their `location` string — one location (e.g. "Leasing -
// PM Office") can have multiple items (a computer AND a phone), each its own
// toggleable line, but they should render as ONE location card, not one card
// per item. Every consumer (admin authoring, both client-facing toggle
// surfaces, the printed doc) calls this SAME helper so grouping can never
// drift between them. `idx` on each entry is the item's index in the
// ORIGINAL flat array — callers key selection/qty state and updateFfeItem
// calls off that, not off any position within the group.
// A blank/untyped location is kept as its OWN single-item group (keyed by
// its index, not the empty string) so two different brand-new rows a staff
// member is still mid-typing don't silently merge into one "no location"
// group before either has a real name.
export function groupFfeItemsByLocation(items) {
    const rows = Array.isArray(items) ? items : [];
    const order = [];
    const map = new Map();
    rows.forEach((item, idx) => {
        const raw = (item.location || '').trim();
        const key = raw || `__blank_${idx}`;
        if (!map.has(key)) { map.set(key, { location: item.location || '', entries: [] }); order.push(key); }
        map.get(key).entries.push({ item, idx });
    });
    return order.map((key) => map.get(key));
}

// Real, currently-sold equipment across every FFE - A/V and Computers
// category — the population the admin "+ Insert device" picker draws from
// (generalized 2026-07-29 from an earlier phones-only catalog, per explicit
// staff direction: "for FFE i need a catalogue, include the lenovo and the
// TV" / "instead of a dropdown 'Insert a real phone model' it should be
// from a catalogue 'Insert device'"), then extended same-day with a real
// enterprise MFP printer category ("All research HP, brothers, and epson
// Enterprise MFP printers models/prices"). `category` is one of 'computer',
// 'tv', 'printer', 'voip_phone', 'teams_phone' — see
// FFE_EQUIPMENT_CATEGORY_LABEL for the grouped picker labels. Every
// computer/TV entry's FIRST row in its category is a
// real item already staff-priced into a live BuildLab proposal (Lenovo AIO
// $1,299.99, TCL 75" TV $1,179.99) — treated as ground truth, matched to
// its real Lenovo/TCL SKU rather than re-priced; every other row was
// independently researched and each imageUrl verified with a real
// `curl -sI` HTTP 200 + image content-type check before being included
// (the lesson from the two null-imageUrl phone entries below, whose CDW
// links 404 when hotlinked) — checked 2026-07-29, prices drift and vary by
// reseller/region, same caveat as the UniFi catalog above; verify before
// quoting a client.
export const FFE_EQUIPMENT_CATALOG = [
    // Computers — ground truth first (real staff-priced line item), then
    // 3 more real Lenovo AIO tiers (budget/mid/high) for real choice.
    { model: 'F0JD0023US', product: 'Lenovo IdeaCentre 27" All-In-One Touchscreen Desktop — Ryzen AI 5 330, 16GB DDR5, 1TB SSD, Windows 11', manufacturer: 'Lenovo', category: 'computer', priceUsd: 1299.99, imageUrl: 'https://gdx-assets.costco.com/adobe/assets/urn:aaid:aem:8926820d-b636-41d6-8c09-9149454dd911/as/4201024138-847__1.avif?width=727&height=727&fit=contain', specs: 'AMD Ryzen AI 5 330 (4C/8T), 16GB SODIMM DDR5-5600, 1TB M.2 NVMe SSD, 27" FHD (1920x1080) IPS touch, Windows 11 Home' },
    { model: 'F0HQ0060US', product: 'Lenovo IdeaCentre 27" All-in-One Touchscreen Desktop — Ryzen 5 8645HS, 16GB RAM, 1TB SSD, Windows 11', manufacturer: 'Lenovo', category: 'computer', priceUsd: 699.97, imageUrl: 'https://gdx-assets.costco.com/adobe/assets/urn:aaid:aem:8f126eb8-0d04-4578-8630-fedf19456a39/as/qhms7gqvnzgtvs56js9vt5kt__4000362386-847__1.avif?width=727&height=727&fit=contain', specs: 'AMD Ryzen 5 8645HS (6-core), 16GB DDR5-4800, 1TB M.2 SSD, 27" FHD IPS touch, Windows 11 Home — budget tier' },
    { model: 'F0JD001VUS', product: 'Lenovo IdeaCentre AIO 27" FHD IPS Touch-Screen All-In-One — Ryzen AI 5 330, 16GB Memory, 512GB Storage', manufacturer: 'Lenovo', category: 'computer', priceUsd: 1359.99, imageUrl: 'https://pisces.bbystatic.com/image2/BestBuy_US/images/products/6672/6672521_sd.jpg', specs: 'AMD Ryzen AI 5 330, 16GB RAM, 512GB SSD, 27" FHD (1920x1080) touch, Windows 11 Home — mid tier' },
    { model: 'F0JH0004US', product: 'Lenovo IdeaCentre AIO 27" 2K IPS Touch-Screen All-In-One — Core Ultra 7 355, 16GB Memory, 1TB Storage', manufacturer: 'Lenovo', category: 'computer', priceUsd: 1839.99, imageUrl: 'https://pisces.bbystatic.com/image2/BestBuy_US/images/products/6672/6672523_sd.jpg', specs: 'Intel Core Ultra 7 355, 16GB RAM, 1TB SSD, 27" QHD (2560x1440) IPS touch, Windows 11 Home — higher-end tier' },
    // TVs — ground truth first (real staff-priced line item, price
    // includes BuildLab's bundled 3yr warranty + wall mount/install, not
    // just the bare TV — TCL's own bare-TV price is ~$649.99), then 3 more
    // real options spanning smaller/mid/larger sizes and brands.
    { model: '75S551G', product: 'TCL 75" S Class 4K UHD HDR LED Smart TV with Google TV', manufacturer: 'TCL', category: 'tv', priceUsd: 1179.99, imageUrl: 'https://us.tcl.com/cdn/shop/files/0175S551G-Front.png?v=1764064421', specs: '75" class, 4K UHD (3840x2160), LED, HDR10/HDR10+, Google TV — price includes 3yr warranty + wall mount/install' },
    { model: 'QN55Q7FAAFXZA', product: '55" Class QLED Q7F 4K Samsung Vision AI Smart TV (2025)', manufacturer: 'Samsung', category: 'tv', priceUsd: 529.99, imageUrl: 'https://images.samsung.com/is/image/samsung/p6pim/us/qn55q7faafxza/gallery/us-qled-q7f-543508-543508-qn55q7faafxza-545863873', specs: '55" class, 4K UHD, QLED (Quantum Dot), Q4 AI Processor, Vision AI smart platform' },
    { model: 'K65S20M2', product: 'Sony BRAVIA 2 II 65" 4K HDR Smart LED TV', manufacturer: 'Sony', category: 'tv', priceUsd: 698.00, imageUrl: 'https://www.bhphotovideo.com/images/images500x500/sony_k65s20_bravia_2_ii_65_1743702037_1883938.jpg', specs: '65" class, 4K UHD (3840x2160) LED, HDR10 & HLG, 60Hz native refresh, Google TV' },
    { model: '85QM6K', product: 'TCL 85" QM6K Series QD-Mini LED QLED 4K UHD Smart TV with Google TV', manufacturer: 'TCL', category: 'tv', priceUsd: 1199.99, imageUrl: 'https://us.tcl.com/cdn/shop/files/0175QM6K_Front.png?v=1763551274', specs: '85" class, 4K UHD, QD-Mini LED/QLED, up to 144Hz, Dolby Vision/HDR10+, Google TV' },
    // Enterprise MFPs — 2 real models per brand (HP/Brother/Epson), each a
    // compact workgroup tier + a higher-volume departmental tier, for a
    // business center/leasing office. HP.com's own store pages blocked
    // automated fetching, so both HP prices/specs came from real third-party
    // retailers (Staples, Target) instead of hp.com directly — spot-check
    // against hp.com if exact current pricing matters. Epson doesn't post
    // MSRPs on its own product pages for either model here — WF-M5899's
    // price is Amazon's (more volatile than direct-reseller pricing
    // elsewhere in this catalog; that listing showed low stock), WF-C5890's
    // is Epson's own site but a promotional/discounted price, not a stated
    // MSRP — verify both before quoting.
    { model: '4RA82F#BGJ', product: 'HP LaserJet Pro MFP 4301fdw Wireless Color All-In-One Printer', manufacturer: 'HP', category: 'printer', priceUsd: 659.99, imageUrl: 'https://www.staples-3p.com/s7/is/image/Staples/0525A0B2-480F-4027-967DF203D12B5877_sc7', specs: '35 ppm color/mono, print/scan/copy/fax, color laser, auto duplex, Wi-Fi + wired network — compact workgroup tier' },
    { model: '6QN29A#BGJ', product: 'HP Color LaserJet Enterprise MFP 5800dn', manufacturer: 'HP', category: 'printer', priceUsd: 2619.00, imageUrl: 'https://target.scene7.com/is/image/Target/GUEST_63b2f28d-c157-4e4c-a3f4-7a8bea79fee4?wid=300&hei=300&fmt=pjpeg', specs: '45 ppm color/mono, print/scan/copy, color laser, auto duplex, Wi-Fi + Ethernet, 100-sheet ADF — higher-volume departmental tier' },
    { model: 'MFC-L5915DW', product: 'Brother MFC-L5915DW Business Monochrome Laser All-in-One Printer', manufacturer: 'Brother', category: 'printer', priceUsd: 589.99, imageUrl: 'https://assets.brother.com/transform/0d9ebbfe-4709-431e-af05-d0c66b634843/20493_MFC-L5915DW-Front-jpg', specs: '50 ppm mono, print/scan/copy/fax, monochrome laser, auto duplex, Gigabit Ethernet + dual-band Wi-Fi — compact workgroup tier' },
    { model: 'MFC-L9610CDN', product: 'Brother MFC-L9610CDN Enterprise Color Laser All-in-One Printer', manufacturer: 'Brother', category: 'printer', priceUsd: 1199.99, imageUrl: 'https://assets.brother.com/transform/3c135d71-09bf-4814-a9cf-c2b58e6719d7/18584_MFC-L9610CDN-Spinner-1-1500x1500-jpg', specs: '42 ppm color, print/scan/copy/fax, color laser, auto duplex, Gigabit Ethernet (optional Wi-Fi module) — higher-volume departmental tier' },
    { model: 'C11CK76201', product: 'Epson WorkForce Pro WF-M5899 Wired & Wireless Inkjet Multifunction Printer (Monochrome)', manufacturer: 'Epson', category: 'printer', priceUsd: 575.00, imageUrl: 'https://m.media-amazon.com/images/I/71GXlefLBsL._AC_SX425_.jpg', specs: '25 ppm mono, print/scan/copy/fax, monochrome inkjet, auto duplex, Wi-Fi + Ethernet — compact workgroup tier' },
    { model: 'C11CK23201', product: 'Epson WorkForce Pro WF-C5890 Color MFP', manufacturer: 'Epson', category: 'printer', priceUsd: 910.00, imageUrl: 'https://mediaserver.goepson.com/adaptivemedia/rendition?id=2730280128cfc7162fb81d9a8306ebf539b00ec5&vid=2730280128cfc7162fb81d9a8306ebf539b00ec5&prid=1200Wx1200H&clid=SAPDAM&prclid=banner&assetDescr=WF-C5890_headon_690x460', specs: '25 ppm color/mono, print/scan/copy/fax, color inkjet, auto duplex, Wi-Fi + Ethernet, 1,830-sheet max capacity — higher-volume departmental tier' },
    { model: 'SIP-T33G', product: 'Yealink T33G IP Phone', manufacturer: 'Yealink', category: 'voip_phone', priceUsd: 83.75, imageUrl: 'https://cdn11.bigcommerce.com/s-u3uxlvxq3h/products/9278/images/42273/Yealink-T33G-T33P-Left__11474.1680897295.386.513.jpg?c=1', specs: '2.4" 320x240 color LCD, dual Gigabit Ethernet, 802.3af PoE, 4 SIP accounts — entry-level desk phone' },
    // CDW's own product-detail image URL 404s when hotlinked (confirmed
    // 2026-07-29) — null rather than a broken link, same convention as
    // AREA_OF_REFUGE_UPS_BATTERY.imageUrl elsewhere in this file.
    { model: 'SIP-T46U', product: 'Yealink T46U IP Phone', manufacturer: 'Yealink', category: 'voip_phone', priceUsd: 245.00, imageUrl: null, specs: '4.3" color TFT, dual Gigabit Ethernet PoE, 16 SIP accounts, dual USB — mid-range desk phone' },
    { model: 'VVX-250', product: 'Poly VVX 250 Business IP Phone', manufacturer: 'Poly', category: 'voip_phone', priceUsd: 145.99, imageUrl: 'https://cdn-cjcgl.nitrocdn.com/KAulDKpqoklwnUZfGEzRiVmDsqsZfBSv/assets/images/optimized/rev-fa5c9a3/www.headsetsdirect.com/wp-content/uploads/2019/09/Poly-VVX-250-Microban.png', specs: 'Entry 4-line SIP desk phone, 3.5" color display, Poly HD Voice' },
    // See file-header comment above — marketed as "Teams Voice Gateway
    // certified" by resellers, but not on Microsoft's own Teams Certified
    // Phones directory. Kept as voip_phone, not teams_phone.
    { model: 'Edge-E320', product: 'Poly Edge E320 IP Desk Phone', manufacturer: 'Poly', category: 'voip_phone', priceUsd: 161.02, imageUrl: 'https://cdn-cjcgl.nitrocdn.com/KAulDKpqoklwnUZfGEzRiVmDsqsZfBSv/assets/images/optimized/rev-fa5c9a3/www.headsetsdirect.com/wp-content/uploads/2022/08/Poly-Edge-E300-IP-Series.png', specs: '8-line-key SIP phone, Bluetooth 5.0, NoiseBlockAI' },
    // Amazon third-party marketplace price — more volatile than the
    // direct-reseller pricing on every other row here; verify before
    // quoting.
    { model: 'CP-8865-K9=', product: 'Cisco IP Phone 8865', manufacturer: 'Cisco', category: 'voip_phone', priceUsd: 399.00, imageUrl: 'https://m.media-amazon.com/images/I/61bhB7h8kBL._AC_SL1500_.jpg', specs: '5" color display, 720p two-way video, PoE with integrated Gigabit switch, Bluetooth' },
    // Confirmed genuinely Microsoft Teams Certified (on Microsoft's own
    // directory). Two real prices found for this exact model — $274.99 at
    // CDW (used here, fetched directly), $206.25 at IP Phone Warehouse —
    // reseller pricing varies; verify before quoting.
    // Same CDW hotlink-failure issue as SIP-T46U above.
    { model: 'MP54-E2-TEAMS', product: 'Yealink MP54 E2 for Microsoft Teams', manufacturer: 'Yealink', category: 'teams_phone', priceUsd: 274.99, imageUrl: null, specs: '4" touchscreen, dedicated Teams button, Android-based, Bluetooth/WiFi, PoE — Microsoft Teams Certified' },
    { model: 'C455HD-Teams', product: 'AudioCodes C455HD for Microsoft Teams', manufacturer: 'AudioCodes', category: 'teams_phone', priceUsd: 306.94, imageUrl: 'https://www.audiocodessuperstore.com/v/vspfiles/photos/TEAMS-C455HD-2T.jpg', specs: 'Color touchscreen SIP/Teams phone, dedicated Teams button, Bluetooth — Microsoft Teams Certified' },
    // Confirmed genuinely Microsoft Teams Certified. Real source page
    // shows a $376.38-$395.85 range depending on power/handset
    // configuration — exact single-SKU price not isolated; using the low
    // end, verify the specific configuration before quoting.
    { model: 'CCX505-82Z79AA', product: 'Poly CCX 505 for Microsoft Teams', manufacturer: 'Poly', category: 'teams_phone', priceUsd: 376.38, imageUrl: 'https://cdn-cjcgl.nitrocdn.com/KAulDKpqoklwnUZfGEzRiVmDsqsZfBSv/assets/images/optimized/rev-fa5c9a3/www.headsetsdirect.com/wp-content/uploads/2021/11/Poly-CCX-500-Business-Media-Phone.webp', specs: '5" 720x1280 touchscreen, native on-device Teams app, Acoustic Fence noise blocking — Microsoft Teams Certified' },
];
export function trueFfeEquipment() {
    return FFE_EQUIPMENT_CATALOG;
}
export const FFE_EQUIPMENT_CATEGORY_LABEL = { computer: 'Computers', tv: 'TVs', printer: 'Printers (MFP)', voip_phone: 'VoIP Phone', teams_phone: 'Microsoft Teams Phone' };

export function coaxHomerunAlternate(units) {
    const n = Number(units) || 0;
    const price = Math.round((n * CABLING_RATES.coaxHomerun) / 100) * 100;
    const text = `Add Alternate — Coax Backbone: ${n}× RG6 quad-shield coax home run, IDF → in-unit SMC enclosure, in addition to the fiber microduct pathway in the base scope. Includes cable, connectors, and termination at both ends; test & certify.`;
    return { units: n, price, text };
}

// Real Legrand On-Q structured wiring enclosure hardware, for pricing an SMC
// panel "Add Alternate" when the ISP does NOT supply the physical enclosure
// (the base takeoff's CABLING_RATES.smcEnclosure is a flat placeholder, not
// tied to any specific size/model — see the comment on it above). Material
// prices are Legrand's own published list price (legrand.us, checked 2026-07),
// rounded to the nearest dollar — a real, sourceable number, not a guess.
export const SMC_PANEL_OPTIONS = [
    { key: '14', size: '14"', model: 'Legrand On-Q EN1400', material: 66, label: '14" — EN1400' },
    { key: '28', size: '28"', model: 'Legrand On-Q EN2850', material: 161, label: '28" — EN2850' },
    { key: '42', size: '42"', model: 'Legrand On-Q ENP4250NA', material: 202, label: '42" — ENP4250NA (Wi-Fi ready)' },
];
// Mount, dress cables into the enclosure, label — same order of magnitude as
// this file's other per-unit install-labor lines, on top of the hardware's
// own material cost.
const SMC_PANEL_INSTALL_LABOR = 45;

// Priced the same way as coaxHomerunAlternate() — a staff-authored "Add
// Alternate" line, not folded into the base scope total. Unlike the coax
// alternate (an addition alongside the base scope), this one is a
// REPLACEMENT: it supplies and installs a specific, real SMC enclosure in
// place of the base takeoff's generic smcEnclosure placeholder, for when the
// ISP isn't providing the physical panel. Staff picks the size; remove the
// alternate again if the ISP ends up supplying materials after all.
export function smcPanelAlternate(units, sizeKey = '28') {
    const n = Number(units) || 0;
    const opt = SMC_PANEL_OPTIONS.find((o) => o.key === String(sizeKey)) || SMC_PANEL_OPTIONS[1];
    // HARDWARE_MARKUP applies to the real Legrand material cost only —
    // SMC_PANEL_INSTALL_LABOR is added after, untouched.
    const unitPrice = opt.material * HARDWARE_MARKUP + SMC_PANEL_INSTALL_LABOR;
    const price = Math.round((n * unitPrice) / 100) * 100;
    const text = `Add Alternate — SMC Panel Supply & Install (${opt.size}, ${opt.model}): BuildLab-supplied and -installed structured media center enclosure for all ${n} units, in place of the base scope's generic in-unit panel — for use when the ISP does not provide the physical enclosure. Includes the enclosure, mounting, and cable dressing; excludes active networking equipment (router/switch/ONT/media converter), which remains ISP- or networking-scope work.`;
    return { units: n, size: opt.size, model: opt.model, unitPrice, price, text };
}

export const COAX_LABEL = { none: 'None', living: 'Living room only', all: 'Every room' };

// Client-facing takeoff breakdown — combines a structured_cabling scope's
// persisted `cabling_config` (the staff's actual answers in the interactive
// CablingTakeoff panel: drops/bedroom, coax mode, microduct pathways, and
// SMC panel size/provider) with the request's real unit mix. Reuses
// cablingTakeoff()'s own count math and unitMixDropBreakdown()'s
// per-bedroom-type split so this can never drift from what actually got
// priced — only adds the SMC size/model lookup neither of those already
// expose. Returns null when cfg is absent: a proposal whose
// structured_cabling scope was priced before this feature existed (or where
// staff typed the scope text by hand instead of using the calculator) has
// nothing specific saved to show, which is a meaningfully different state
// from "configured with every default" — so callers should render nothing
// rather than guess.
export function cablingConfigBreakdown(unitMix, cfg) {
    if (!cfg) return null;
    const mix = unitMix && typeof unitMix === 'object' && !Array.isArray(unitMix) ? unitMix : {};
    const { counts } = cablingTakeoff({
        mix,
        dropsPerBedroom: cfg.dropsPerBedroom,
        livingDrops: cfg.livingDrops,
        coax: cfg.coax,
        pathwaysPerUnit: cfg.pathwaysPerUnit,
    });
    const perBedroom = unitMixDropBreakdown(mix, { dropsPerBedroom: cfg.dropsPerBedroom, livingDrops: cfg.livingDrops });
    const smcOption = SMC_PANEL_OPTIONS.find((o) => o.key === String(cfg.smcSize)) || null;
    return {
        counts,
        perBedroom,
        dropsPerBedroom: cfg.dropsPerBedroom,
        livingDrops: cfg.livingDrops,
        coax: cfg.coax,
        pathwaysPerUnit: cfg.pathwaysPerUnit,
        smc: smcOption ? { size: smcOption.size, model: smcOption.model } : null,
        smcProvidedBy: cfg.smcProvidedBy === 'isp' ? 'isp' : 'buildlab',
    };
}

// ---------------------------------------------------------------------------
// Network rack / bill-of-materials builder — for a "networking" (Data/
// Communications) scope's active equipment build-out (firewall, switches,
// patch panels, etc.), as opposed to structured_cabling's passive cable/
// pathway work. Deliberately NOT a fixed hardware catalog like
// SMC_PANEL_OPTIONS — a firewall alone ranges ~$200 to $10,000+ by brand and
// throughput tier, and staff price real per-job vendor quotes, not a lookup
// that would go stale. manufacturer/model/unit_cost are always freeform
// staff entry; category/ru_height/port_count are the only structured fields,
// kept for RU-capacity math and a legible printed equipment schedule.
export const RACK_ITEM_CATEGORIES = [
    { key: 'firewall', label: 'Firewall' },
    { key: 'switch', label: 'Switch' },
    { key: 'patch_panel', label: 'Patch Panel' },
    { key: 'router', label: 'Router' },
    { key: 'wap_controller', label: 'WAP Controller' },
    { key: 'ups', label: 'UPS' },
    { key: 'pdu', label: 'PDU' },
    { key: 'nvr', label: 'NVR' },
    { key: 'storage', label: 'Storage Drive' },
    // The Area of Refuge system's BOSS controller + Call Commander console —
    // the central head-end every refuge_station call station reports to (see
    // AREA_OF_REFUGE_CONTROL_MODULE/_CALL_COMMANDER below). Lives here, not
    // as a floor-plan device, exactly mirroring how a surveillance scope's
    // NVR head-end is a rack_items line rather than a map placement.
    { key: 'refuge_control', label: 'Area of Refuge Control Panel' },
    // An access_control scope's door controller board(s) (e.g. a Mercury/HID
    // multi-door panel) — the head-end every reader/panel device on the
    // floor plan wires back to. Deliberately freeform, not a specific vendor
    // catalog like SMC_PANEL_OPTIONS/UNIFI_CATALOG/AREA_OF_REFUGE_* — real
    // access-control controller vendors (HID/Mercury/Genetec/Software House/
    // Honeywell, etc.) vary too widely for one reference catalog to be
    // defensible, same reasoning as the existing firewall/switch/router
    // categories above.
    // Display label renamed 'Access Control Panel' → 'Door Controller'
    // 2026-07-30 per explicit staff direction: "Ubiquiti EAH-8 should be
    // labeled as Door Controller and not ACCESS CONTROL PANEL." Display-
    // text-only — the `access_control_panel` key, every category check
    // (rackCategoriesFor, AdminProposalPage/FloorplanEditorPage's
    // hasRealHeadEndHardware gates), RACK_ITEM_COLOR's key, and stored
    // rack_items data are all unchanged, same convention as the
    // device_type 'panel' → "Controller" display rename.
    { key: 'access_control_panel', label: 'Door Controller' },
    { key: 'rack_enclosure', label: 'Rack Enclosure' },
    { key: 'cable_mgmt', label: 'Cable Management' },
    // Copper patch cords (switch port → patch panel port) and SFP+/DAC
    // uplink cables — real, priced consumables, not "cable management"
    // (D-rings/velcro/organizers). qty is normally seeded from
    // patchCableSeed()/sfpCableSeed() below (matching the rack's actual
    // switch RJ45/SFP port counts) rather than typed by hand, though staff
    // can still add/edit these manually like any other row.
    { key: 'patch_cable', label: 'Patch Cable' },
    { key: 'sfp_cable', label: 'SFP+ Cable' },
    // A zero-cost-by-convention row for equipment that physically sits in
    // THIS rack but is priced under a different scope's line item (e.g. the
    // surveillance scope's NVR, sitting in the same enclosure as the
    // Data/Communications switch gear) — added 2026-07-29 so the rack
    // elevation diagram can reflect real physical layout (RU space
    // reserved, a free-text label identifying what's there) without
    // double-billing its cost here. unit_cost is exactly as editable as any
    // other row (nothing enforces $0) — staff are trusted to leave it blank/
    // zero here, same "no hand-holding validation" convention this whole
    // Rack Builder already follows for every other field.
    { key: 'placeholder', label: 'Placeholder (priced elsewhere)' },
    { key: 'other', label: 'Other' },
];
const RACK_ITEM_LABEL = Object.fromEntries(RACK_ITEM_CATEGORIES.map((c) => [c.key, c.label]));

// Color-per-category is purely for visual scannability (a technical
// drawing's/list's swatch, not a brand-token surface) — a small fixed
// qualitative palette, distinct enough to tell categories apart at a
// glance. Shared between RackElevation.js's diagram+equipment-index and
// AdminProposalPage.js's Rack Builder list, so the same category always
// reads as the same color everywhere it appears.
export const RACK_ITEM_COLOR = {
    firewall: '#EF4444', switch: '#0EA5A4', patch_panel: '#7C5CFC', router: '#F59E0B',
    wap_controller: '#3B82F6', ups: '#22C55E', pdu: '#EC4899', nvr: '#92400E', storage: '#0284C7',
    // Matches SCOPE_COLOR.area_of_refuge in AdminProposalPage.js/ClientProposalPage.js/
    // ProposalSharePage.js/ProposalDocumentsPage.js, so this rack item reads as
    // "the same system" as its scope's own tab color.
    refuge_control: '#EA580C',
    // Matches SCOPE_COLOR.access_control across the same four surfaces, same
    // "reads as the same system as its own tab color" reasoning.
    access_control_panel: '#F59E0B',
    cable_mgmt: '#64748B',
    patch_cable: '#7C5CFC', sfp_cable: '#0EA5A4',
    // Deliberately muted/dashed-looking (paired with a lighter tone than any
    // real-cost category) — this row is a spatial reference, not billed
    // equipment, and shouldn't visually compete with real line items.
    placeholder: '#CBD5E1',
    other: '#94A3B8',
};

// The Rack Builder (rack_items + RackCatalogPicker + RackElevation) is
// available on both networking scopes (the actual data/comms rack — router,
// switches, patch panels) and surveillance scopes (the camera system's own
// head-end — NVR + storage drives). `nvr`/`storage` are deliberately NOT
// offered on a networking scope — that equipment belongs to whichever
// system it serves, not wherever staff happen to be adding rack gear;
// mixing an NVR into the Data/Communications BOM double-books it against
// two different systems' scopes of work.
// area_of_refuge joins this list for the same reason surveillance did — its
// BOSS controller + Call Commander console are head-end equipment
// (rack_items lines), not a floor-plan device.
// access_control joins for the same reason again — its door controller
// board(s) are head-end equipment, not a floor-plan device (readers/panels
// on the floor plan wire back to this rack gear).
export const RACK_BUILDER_SCOPE_TYPES = ['networking', 'surveillance', 'area_of_refuge', 'access_control'];
export function rackCategoriesFor(scopeType) {
    if (scopeType === 'networking') return RACK_ITEM_CATEGORIES.filter((c) => c.key !== 'nvr' && c.key !== 'storage');
    if (scopeType === 'access_control') return RACK_ITEM_CATEGORIES.filter((c) => ['access_control_panel', 'ups', 'pdu', 'rack_enclosure', 'cable_mgmt', 'other'].includes(c.key));
    // area_of_refuge's rack is just the BOSS/Call Commander head-end plus the
    // generic power/enclosure lines any rack might need — none of the
    // networking/surveillance-specific categories (firewall, switch, NVR,
    // storage, etc.) belong here.
    if (scopeType === 'area_of_refuge') return RACK_ITEM_CATEGORIES.filter((c) => ['refuge_control', 'ups', 'pdu', 'rack_enclosure', 'cable_mgmt', 'other'].includes(c.key));
    return RACK_ITEM_CATEGORIES;
}

// Mounting style — meaningful only for a rack_enclosure line (item.mount_type),
// covering BuildLab's realistic IDF/MPOE-closet range rather than every rack
// format that exists (no portable/desktop/etc.).
export const RACK_MOUNT_TYPES = [
    { key: 'floor_standing', label: 'Floor-standing' },
    { key: 'wall_mounted', label: 'Wall-mounted' },
    { key: 'open_frame', label: 'Open-frame' },
];
// Common off-the-shelf enclosure heights, for a one-click fill of a
// rack_enclosure row's ru_height — the field itself stays a free number
// input (real jobs sometimes need a non-standard size), these are just
// convenience presets, same role SMC_PANEL_OPTIONS' chips play for size.
export const RACK_SIZE_PRESETS_U = [6, 9, 12, 15, 24, 32, 42];

// A rack_enclosure row only counts as a real, separately-drawable physical
// rack once it has an actual size (qty*ru_height > 0) — a freshly-added
// enclosure row staff hasn't sized yet shouldn't fragment equipment into a
// phantom zero-capacity rack. Shared between RackElevation.js's per-rack
// sectioning and AdminProposalPage.js's "add to" rack picker so both agree
// on exactly which rows count — keep any change to this rule in sync there.
export function sizedRackEnclosureIndices(items) {
    const rows = Array.isArray(items) ? items : [];
    const indices = [];
    rows.forEach((r, i) => {
        if (r.category === 'rack_enclosure' && (Number(r.qty) || 0) * (Number(r.ru_height) || 0) > 0) indices.push(i);
    });
    return indices;
}

// Pure computation over a buildlab_proposal_scopes.rack_items array — same
// {total/text}-shape precedent as cablingTakeoff()/unitMixDropBreakdown(), so
// the "Apply $X" price, the inserted scope text, and an "Add as alternate"
// seed can never drift from each other or from what's actually in the list.
// A rack_enclosure line (if any) sets capacityRu; every other line's
// ru_height contributes to consumedRu — capacity is advisory (many jobs
// reuse an existing rack with no enclosure line at all), never blocking.
export function rackBuilderSummary(items, markupPct = null) {
    const rows = Array.isArray(items) ? items : [];
    const equipment = rows.filter((r) => r.category !== 'rack_enclosure');
    const enclosures = rows.filter((r) => r.category === 'rack_enclosure');
    // The markup applies to the SUMMED total only — a row's own unit_cost
    // (staff-editable input, or catalog-seeded) always stays the real,
    // unmarked-up vendor/distributor cost; this is the one place that real
    // cost becomes a client-facing quoted price. markupPct is the scope's
    // own equipment_markup_pct override (real staff request 2026-07-30) —
    // absent/null keeps the flat HARDWARE_MARKUP, so every pre-existing
    // caller is byte-identical (see markupMultiplier).
    const mult = markupMultiplier(markupPct);
    const rawTotal = rows.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.unit_cost) || 0), 0);
    const total = rawTotal * mult;
    const consumedRu = equipment.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.ru_height) || 0), 0);
    const capacityRu = enclosures.length ? enclosures.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.ru_height) || 0), 0) : null;
    const lines = equipment
        .filter((r) => Number(r.qty) > 0)
        .map((r) => {
            const bits = [`${r.qty}× ${RACK_ITEM_LABEL[r.category] || r.category}`];
            const mm = [r.manufacturer, r.model].filter(Boolean).join(' ');
            if (mm) bits.push(`— ${mm}`);
            const specs = [r.ru_height ? `${r.ru_height}U` : null, r.port_count ? `${r.port_count} port` : null].filter(Boolean);
            if (specs.length) bits.push(`(${specs.join(', ')})`);
            return `• ${bits.join(' ')}`;
        });
    const mountLabel = RACK_MOUNT_TYPES.find((m) => m.key === enclosures[0]?.mount_type)?.label;
    const text = enclosures.length
        ? [`Equipment schedule (${capacityRu}U${mountLabel ? ` ${mountLabel.toLowerCase()}` : ''} rack):`, ...lines].join('\n')
        : ['Equipment schedule:', ...lines].join('\n');
    const header = enclosures.length ? `${capacityRu}U${mountLabel ? ` ${mountLabel.toLowerCase()}` : ''} rack` : null;
    // Enclosure rows lead `items` (the rack itself is the first line of a
    // real BOM), then the equipment that goes in it — enclosures were
    // excluded from this table entirely until 2026-07-30 (they only fed the
    // "12U wall-mount rack" header), per real staff direction the same day:
    // "It needs to include the rack builder equipment including the racks."
    // This also closes a latent gap: `total` above always summed enclosure
    // cost, so the visible rows under-listed what the total charged — and
    // scopeEquipmentHardwareTotal() (the Self-Procure deduction, which sums
    // qty × price over these rows) silently missed the racks' cost too.
    const placedEquipment = [...enclosures, ...equipment].filter((r) => Number(r.qty) > 0);
    const perRow = placedEquipment.map((r) => ({
        qty: Number(r.qty) || 0,
        categoryLabel: RACK_ITEM_LABEL[r.category] || r.category,
        manufacturerModel: [r.manufacturer, r.model].filter(Boolean).join(' '),
        specs: [
            r.ru_height ? `${r.ru_height}U` : null,
            r.category === 'rack_enclosure' ? RACK_MOUNT_TYPES.find((m) => m.key === r.mount_type)?.label : null,
            r.port_count ? `${r.port_count} port` : null,
        ].filter(Boolean).join(', '),
        // Per-UNIT price (Qty × Price = the row's real extended cost) — same
        // HARDWARE_MARKUP the summed `total` above applies, just not yet
        // multiplied by qty, so EquipmentDeviceSchedule.js's Price column
        // reads consistently as "cost per unit" for both rack rows here and
        // device rows (deviceInstalledCost() is likewise per-unit), matching
        // a standard Qty/Description/Unit-Price bill-of-materials layout.
        // client_unit_price fallback (real bug report 2026-07-30, same day
        // this column shipped: "the NVR Unit Price doesnt show up in the
        // proposal" — 7175 Sitio Caballero's lone Reolink RLN8-410 rack row
        // showed $0 on the client portal/share page while its 7 camera rows
        // priced fine): every client-facing surface receives a SANITIZED
        // rack_items copy with raw vendor unit_cost stripped server-side
        // (get_scopes_rack_items_public / the buildlab-shared-proposal edge
        // function), so computing from unit_cost alone zeroed every rack
        // row's price everywhere except the staff-only pages. Both
        // sanitizers now substitute client_unit_price — the ALREADY
        // MARKED-UP round(unit_cost × markup) figure, the exact number
        // staff chose to show clients when this column was added — so it
        // must NOT be multiplied by ANY markup again here, `mult` included:
        // since the equipment_markup_pct override shipped (2026-07-30) both
        // sanitizers compute it from the scope's OWN pct (falling back to
        // the flat 1.20), so re-applying `mult` on top would double-mark
        // every client-facing rack row. unit_cost wins when present (staff
        // surfaces, raw data) so admin behavior is byte-identical to before
        // the fallback existed.
        price: r.unit_cost == null && r.client_unit_price != null
            ? Math.round(Number(r.client_unit_price) || 0)
            : Math.round((Number(r.unit_cost) || 0) * mult),
    }));
    // Aggregate identical items into one line with a summed qty (real staff
    // direction 2026-07-30, on Bridge networking's schedule showing five
    // separate qty-1 patch-panel rows and two qty-1 rows per switch model:
    // "why are each equipment individual instead of having Quantities for
    // similar ones?"). The Rack Builder deliberately stores ONE ROW PER
    // PHYSICAL UNIT — each row carries its own rack position for the
    // RackElevation diagram — but a bill of materials shouldn't echo that
    // storage shape. Identity = the VISIBLE columns only: category +
    // manufacturer/model + PER-UNIT PRICE. Two units of the same model at
    // different real unit costs stay separate lines (they genuinely cost
    // different amounts, and the difference is visible) instead of silently
    // averaging — but `specs` is deliberately NOT part of the identity: the
    // schedule table has no Specs column (removed 2026-07-29 per staff), so
    // rows split only by an invisible spec difference render as exact
    // duplicates (follow-up staff report the same day, two ECHOGEAR racks
    // differing only in configured RU: "i still see duplicates"). The first-
    // seen row's specs ride along on the merged line. First-seen order is
    // preserved, which also keeps enclosures leading the list. qty × price
    // sums are exactly invariant under this grouping, so
    // scopeEquipmentHardwareTotal() (the Self-Procure deduction, summed over
    // these rows) is unchanged.
    const items_ = [];
    const byIdentity = new Map();
    perRow.forEach((row) => {
        const key = [row.categoryLabel, row.manufacturerModel, row.price].join('|');
        const existing = byIdentity.get(key);
        if (existing) {
            existing.qty += row.qty;
        } else {
            byIdentity.set(key, row);
            items_.push(row);
        }
    });
    return { total: Math.round(total), consumedRu, capacityRu, text, header, items: items_ };
}

// One scope's total MARKED-UP equipment/hardware dollars — the single shared
// deduction figure behind the client-facing "Self Procure" toggle (real staff
// request 2026-07-30: "I want to give Residential Clients to be able to see
// the Equipment Schedule and pricing and a toggle to 'Self Procure' which
// should update the total price to just labor/materials"). Toggled ON, the
// scope's displayed price becomes price − this figure (clamped ≥ 0 by the
// caller): the client buys the hardware themselves and pays BuildLab only
// for labor, cabling/materials, and head-end install labor.
//
// Deliberately the MARKED-UP total, not raw vendor cost — staff was offered
// a raw-cost deduction and didn't take it — so the deduction is visibly the
// sum of the same Unit Price figures the Equipment Schedule itemizes right
// above the toggle, not a third number a client can't reconcile.
//
// Both halves REUSE the schedule's own per-row math rather than re-deriving
// it — the markup/client_unit_price subtleties here changed twice on
// 2026-07-30 alone (equipment_markup_pct override, sanitized-shape
// client_unit_price fallback, price_override becoming raw pre-markup
// hardware) and a second independent derivation would drift:
// - Rack rows: rackBuilderSummary(items, markupPct).items[].price — the
//   exact per-unit figure the schedule's rack rows display, which already
//   resolves unit_cost × markup on staff surfaces AND the server-computed
//   client_unit_price on sanitized client-facing copies (where unit_cost
//   is stripped and re-marking would double-count).
// - Devices: deviceInstalledCost(d, markupPct).hardware — the HARDWARE
//   component only, never .total: the schedule's device rows display the
//   full installed cost (hardware + labor + cabling), but a self-procuring
//   client still owes the labor and cable-run portions, so only hardware
//   leaves the price. This is also what makes price_override behave right:
//   it's raw pre-markup hardware, so an overridden camera deducts
//   override × markup while its real install labor stays billed.
// A labor-only device (refuge_commander's $0 hardware) contributes nothing,
// so a scope with no real hardware returns 0 and callers hide the toggle
// rather than offering a meaningless $0 deduction.
export function scopeEquipmentHardwareTotal(rackItems, devices, markupPct = null) {
    const rackTotal = rackBuilderSummary(rackItems, markupPct).items
        .reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
    const deviceTotal = (Array.isArray(devices) ? devices : [])
        .reduce((a, d) => a + deviceInstalledCost(d, markupPct).hardware, 0);
    return Math.round(rackTotal + deviceTotal);
}

// Whether RackElevation.js will already render a visual rack diagram for this
// scope — EquipmentDeviceSchedule.js uses this to suppress its own equipment
// listing rather than duplicate what's drawn right above it (same rule
// RackElevation.js itself uses to decide whether it has anything to draw).
export function rackElevationWouldRender(scopeType, items) {
    return RACK_BUILDER_SCOPE_TYPES.includes(scopeType) && sizedRackEnclosureIndices(items).length > 0;
}

// Per-item Network Head-End Install Labor for a networking (Data/
// Communications) scope's rack build-out — staff-approved planning figures
// (hours × the $100/hr labor rate, dollars baked in), approved 2026-07-30.
// Networking previously charged NO head-end labor at all by design (WAP
// install labor lives in the Data Drops section; the Rack Builder priced
// hardware only) — this closes that gap with labor that scales with what's
// actually in the rack, the way a real head-end build does. Keys double as
// the buildlab_rate_settings rows staff tune from the CRM's Rates tab
// (RateSettingsTab.js) — these constants are the seed values and the
// fallback for the brief window before a page's rateSettings fetch
// resolves, same convention as SURVEILLANCE_HEADEND_INSTALL_LABOR above.
export const NETWORKING_HEADEND_LABOR_RATES = {
    // Per rack/enclosure — mount, level, ground (1.5 hr).
    networking_headend_labor_rack_enclosure: 150,
    // Per 48-port switch — rack, power, uplink, VLAN/PoE config, controller
    // adoption (2 hr).
    networking_headend_labor_switch_48: 200,
    // Per 24-port-or-smaller switch — same scope of work (1.5 hr).
    networking_headend_labor_switch_24: 150,
    // Per router/cloud gateway — WAN setup, VLANs, firewall, controller
    // adoption (3 hr).
    networking_headend_labor_router: 300,
    // Per patch panel — mount + dress ONLY; the drop terminations landing on
    // it are already billed in Data Drops labor (1 hr).
    networking_headend_labor_patch_panel: 100,
    // Per UPS — rack, power distribution, test (1 hr).
    networking_headend_labor_ups: 100,
    // Flat once-per-scope adder — network-wide config, testing, labeling,
    // documentation (4 hr). Only applies when at least one billable item
    // above is actually in the rack.
    networking_headend_labor_system: 400,
};
// Pure computation over a networking scope's rack_items (same array shape
// rackBuilderSummary() above iterates — category/qty/port_count/model per
// row). Any category without an approved rate (placeholder, patch/SFP
// cables, cable_mgmt, firewall, wap_controller, other) contributes $0 —
// no rate was approved for it, and this file never invents one. Switch
// port-count rule: the row's own structured port_count field decides when
// set (>= 48 → the 48-port rate, else the 24-port-or-smaller rate); a row
// with no port_count falls back to sniffing the freeform model string for
// "48". rateSettings is the same {key: value_usd-as-number} map
// surveillanceHeadendInstallLabor() consumes (AdminProposalPage.js /
// FloorplanEditorPage.js's buildlab_rate_settings fetch), with the
// NETWORKING_HEADEND_LABOR_RATES seed values as the not-yet-loaded
// fallback. Returns { total, lines: [{label, qty, rate, amount}],
// systemAdder } — `total` is the one number every consumer prices from;
// lines/systemAdder exist so the Scope Labor card can show line detail.
export function networkingHeadendInstallLabor(rackItems, rateSettings) {
    const rows = Array.isArray(rackItems) ? rackItems : [];
    const rate = (key) => {
        const v = rateSettings?.[key];
        return typeof v === 'number' && !Number.isNaN(v) ? v : NETWORKING_HEADEND_LABOR_RATES[key];
    };
    const lines = [];
    const byLabel = new Map();
    rows.forEach((r) => {
        const qty = Number(r.qty) || 0;
        if (!qty) return;
        let key = null, label = null;
        if (r.category === 'rack_enclosure') {
            key = 'networking_headend_labor_rack_enclosure'; label = 'Rack enclosure';
        } else if (r.category === 'switch') {
            const ports = Number(r.port_count) || 0;
            const is48 = ports > 0 ? ports >= 48 : /48/.test(String(r.model || ''));
            key = is48 ? 'networking_headend_labor_switch_48' : 'networking_headend_labor_switch_24';
            label = is48 ? '48-port switch' : '24-port switch';
        } else if (r.category === 'router') {
            key = 'networking_headend_labor_router'; label = 'Router / cloud gateway';
        } else if (r.category === 'patch_panel') {
            key = 'networking_headend_labor_patch_panel'; label = 'Patch panel';
        } else if (r.category === 'ups') {
            key = 'networking_headend_labor_ups'; label = 'UPS';
        }
        if (!key) return;
        const rateVal = rate(key);
        const existing = byLabel.get(label);
        if (existing) {
            existing.qty += qty;
            existing.amount = existing.qty * existing.rate;
        } else {
            const line = { label, qty, rate: rateVal, amount: qty * rateVal };
            byLabel.set(label, line);
            lines.push(line);
        }
    });
    const systemAdder = lines.length ? rate('networking_headend_labor_system') : 0;
    const total = lines.reduce((a, l) => a + l.amount, 0) + systemAdder;
    return { total, lines, systemAdder };
}

// Real, currently-sold cable consumables for the two seed helpers below.
// Priced per unit in the row's own unit_cost field, same convention as
// every other rack item — HARDWARE_MARKUP applies once over the whole
// rack's total in rackBuilderSummary(), not here.
// Cat6 patch cord, 1ft, single-unit list price — CableWholesale.com Part
// #10X8-02201, checked 2026-07-29 (real bulk tiers run lower: $1.96 at
// 100pc, $1.68 at 500pc — staff can lower the seeded unit_cost for a large
// job).
export const PATCH_CABLE_PRICE = 2.18;
// Ubiquiti 10G SFP+ Direct Attach Cable, shortest/most common in-rack
// length — model UACC-DAC-SFP10-0.5M, store.ui.com, checked 2026-07-29.
export const SFP_CABLE_PRICE = 13.00;

// Seeds (or, if one already exists, recomputes) a "Patch Cable" rack_items
// row sized 1:1 to the rack's own real switch port count (qty × port_count
// summed across every category:'switch' row) — the standard assumption for
// a fully-populated switch: one patch cord per port, switch → patch panel
// or switch → device. Returns null when there's nothing to size from yet
// (no switches, or none with a port_count set) rather than seeding a
// zero-qty row. Deliberately scoped to switches only, per explicit staff
// direction — patch panels/other rack gear don't generate their own patch
// cord count the way a switch's port count does.
export function patchCableSeed(items) {
    const rows = Array.isArray(items) ? items : [];
    const totalPorts = rows.reduce((a, r) => a + (r.category === 'switch' ? (Number(r.qty) || 0) * (Number(r.port_count) || 0) : 0), 0);
    if (totalPorts <= 0) return null;
    return { category: 'patch_cable', manufacturer: 'CableWholesale', model: 'Cat6 Patch Cable (1ft)', qty: totalPorts, unit_cost: PATCH_CABLE_PRICE, ru_height: null, port_count: null, notes: `1 per switch port (${totalPorts} total)` };
}

// Seeds (or recomputes) an "SFP+ Cable" row sized to EVERY rack item's own
// sfp_port_count — not just switches, per explicit staff direction: a
// firewall/router/NVR's own SFP+ uplink needs the same DAC cable a
// switch's does. Returns null when nothing in the rack has a confirmed SFP
// port count yet (see UNIFI_CATALOG's sfpPortCount field — left unset
// rather than guessed for models whose real SFP count isn't confirmed).
export function sfpCableSeed(items) {
    const rows = Array.isArray(items) ? items : [];
    const totalSfpPorts = rows.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.sfp_port_count) || 0), 0);
    if (totalSfpPorts <= 0) return null;
    return { category: 'sfp_cable', manufacturer: 'Ubiquiti', model: 'UACC-DAC-SFP10-0.5M', qty: totalSfpPorts, unit_cost: SFP_CABLE_PRICE, ru_height: null, port_count: null, notes: `1 per SFP+ port across all rack equipment (${totalSfpPorts} total)` };
}

// ---------------------------------------------------------------------------
// Area of Refuge (RATH SmartCommand AOR) — a code-required two-way emergency
// communication system for stairwell landings/safe zones (IBC 1009.6.5, ADA
// Section 207). Two physically distinct hardware roles: a CALL STATION at
// each area-of-refuge location (a floor-plan device — see refuge_station in
// DEVICE_RATES above and access_subtype standard/weatherproof) and a CENTRAL
// HEAD-END every call station reports to (TWO rack_items lines, not a
// floor-plan device — same head-end pattern as a surveillance scope's NVR):
// a BOSS controller (dispatch/supervision) plus a Call Commander (the
// human-operator console — a BOSS with no console for anyone to actually
// answer a call is an incomplete system, so both get seeded together, never
// just one). Real RATH/Avire hardware — RATH Communications was acquired by
// Halma in 2019 and now trades under the AVIRE brand (avire-global.com);
// prices confirmed via BEC Audio / BEC Integrated Solutions, an authorized
// RATH/Avire distributor, 2026-07-28 — distributor pricing varies and
// changes, always re-verify before quoting a client, same caveat as
// UNIFI_CATALOG/EV_CHARGER_OPTIONS. This brand was staff's explicit choice
// over what BuildLab's ADI Global account actually stocks (Alpha
// Communications' AlphaRefuge line, confirmed live on ADI 2026-07-28) — RATH/
// Avire hardware is sourced through a different distributor, not ADI; flag
// this to whoever places the actual order. Deliberately its own small
// dataset, not folded into UNIFI_CATALOG (Ubiquiti-only) or RackCatalogPicker
// — staff enter the head-end as freeform rack_items rows (manufacturer/
// model/cost, same as any other non-cataloged rack item) using these real
// numbers as reference; this dataset exists to source DEVICE_RATES.
// refuge_station and give staff/UI copy a real number to cite, not to drive
// a catalog picker.
//
// Signage: IBC 1009.6.5 requires a sign at each area-of-refuge door reading
// "AREA OF REFUGE" with the International Symbol of Accessibility, PLUS
// separate tactile (raised-letter/Braille) signage per ICC A117.1 — two
// distinct requirements, not one sign covering both. Rather than a separate
// floor-plan device/marker (there's no code-driven reason to place signage
// anywhere other than 1:1 with each call station), a real per-location sign
// kit — one visual ID sign + one tactile/instructional sign, see
// AREA_OF_REFUGE_SIGNAGE below — is folded directly into DEVICE_RATES.
// refuge_station's installed price, so "signage on every floor" falls out
// automatically from however many call stations already exist on the floor
// plan, with zero new schema or UI.
export const AREA_OF_REFUGE_CALL_STATIONS = [
    {
        key: '3300ss',
        model: 'RATH SmartCommand 3300SS',
        sku: '3300SS',
        subtypeKey: 'standard',
        priceUsd: 633.65,
        note: 'Indoor surface-mount call box, stainless steel, hands-free two-way voice ("mushroom button") — confirmed via BEC Audio 2026-07-28.',
        imageUrl: 'https://www.avire-global.com/wp-content/uploads/sites/10/2023/08/3300SS-Surface-Mount.png',
    },
    {
        key: '3300fsn',
        model: 'RATH SmartCommand 3300FSN',
        sku: '3300FSN',
        subtypeKey: 'standard',
        priceUsd: 578,
        note: 'Indoor flush-mount alternative to 3300SS — needs the RP7700504 back box ($83) — confirmed via BEC Audio 2026-07-28.',
        imageUrl: 'https://www.avire-global.com/wp-content/uploads/sites/10/2023/08/3300FSN-Flush-Mount.png',
    },
    {
        key: '3300s4',
        model: 'RATH SmartCommand 3300S4',
        sku: '3300S4',
        subtypeKey: 'weatherproof',
        priceUsd: 646.70,
        note: 'Exterior/weatherproof call box, NEMA-rated fiberglass housing, -13°F to 131°F — confirmed via BEC Audio 2026-07-28. Unlike Cornell\'s 4202 this has a real confirmed public price.',
        imageUrl: 'https://www.avire-global.com/wp-content/uploads/sites/10/2023/08/3300S4-Exterior-Surface-Mount.png',
    },
];
export const AREA_OF_REFUGE_CONTROL_MODULE = {
    model: 'RATH SmartCommand BOSS 3400',
    sku: 'BOSS-3400',
    priceUsd: 3961.40,
    imageUrl: 'https://www.avire-global.com/en-us/wp-content/uploads/sites/10/2023/08/SmartCommand-BOSS-1.png',
    note: 'Distributor-priced ($3,783.05–$3,961.40 seen across BEC Audio/BEC Integrated Solutions, 2026-07-28) — confirm current pricing before quoting. Supports up to 32 call boxes natively on one dedicated phone line; the same board in a BOSS 3400-128 configuration (two phone lines) scales to 128 — swap this line for that model on a building with more than ~32 stations.',
};
// The BOSS's required operator console — see the header comment above for
// why this always gets seeded alongside AREA_OF_REFUGE_CONTROL_MODULE, never
// on its own.
export const AREA_OF_REFUGE_CALL_COMMANDER = {
    model: 'RATH SmartCommand 3200D Call Commander',
    sku: '3200D',
    priceUsd: 639.45,
    imageUrl: 'https://www.avire-global.com/en-us/wp-content/uploads/sites/10/2023/08/SmartCommand-Call-Commanders.png',
    note: 'Desk-mount operator console (handset + speakerphone, call queuing, location display) — $638.00–$639.45 across BEC Audio/BEC Integrated Solutions, 2026-07-28. Surface (3200S) and flush (3200F) mount variants exist at a higher price point ($1,399–$1,402) if desk placement doesn\'t suit the monitoring location.',
};
// NFPA 72 requires supervised standby power for life-safety communication
// systems — a BOSS with no standby power behind it isn't code-compliant,
// same "incomplete system" reasoning as the Call Commander above, so this
// is seeded alongside the other two, not offered as a separate opt-in.
//
// RATH/Avire's own current "3400 BOSS Series" datasheet (Rev Sep 25, itself
// claiming NFPA 72 2022 / NFPA 101 2024 / IBC-IFC 2024 / ADA compliance,
// confirmed live on avire-global.com 2026-07-28) states the actual backup
// requirement in its own words: "Standby - 24 hours minimum; Active talk
// time - 5 minutes" — NOT the generic 60min-alarm-draw figure this comment
// previously (incorrectly) cited from fire-alarm-panel convention; that
// figure doesn't apply here, this system publishes its own spec. The same
// datasheet gives real power-consumption figures per box count: 10W (1 call
// box), 27W (32 call box, the max this BOSS-3400 variant supports per
// AREA_OF_REFUGE_CONTROL_MODULE's own note), 74W (128 call box, the
// BOSS-3400-128 variant) — this covers the WHOLE loop (the BOSS "Powers the
// Call Commander and all connected call boxes" per the same sheet), not
// just the BOSS unit alone.
//
// The base "1000" unit ships with one RP7701000B battery (82Ah @ 12.5VDC)
// already included (confirmed via RATH's own "1000/2500-PWRUPS Power
// Supply" Installation & Specifications Manual, Ver.3 11/22, avire-
// global.com). Real math, worst case (a fully-loaded 32-box system at 27W):
// 27W / 12.5V = 2.16A. Required = (2.16A × 24hr standby) + (2.16A × 5min
// talk) = 51.84Ah + 0.18Ah ≈ 52.0Ah, against 82Ah available — the single
// included battery alone already covers this with a real ~1.6x margin, not
// a bare pass.
//
// AREA_OF_REFUGE_UPS_BATTERY below is a SECOND 82Ah battery wired in
// parallel to the first (per RATH's own "Extended Battery Layout of Long
// Runtime Applications" wiring instructions in that same manual) — genuine
// extra headroom for battery aging/derating or a larger installation, not
// something the math above says is required to meet spec on a standard
// ≤32-box system. Two real SKUs, sold and priced separately by RATH,
// confirmed via BEC Audio 2026-07-28.
export const AREA_OF_REFUGE_UPS = {
    model: 'RATH SmartCommand 1000 Power Supply with Battery Backup',
    sku: '1000',
    priceUsd: 1653.64,
    imageUrl: 'https://www.becaudio.com/Shop/images/1000.png',
    note: '4A (100VA) supply current; ships with one RP7701000B battery (82Ah @ 12.5VDC) included. RATH/Avire\'s own BOSS 3400 datasheet backup requirement (24hr standby + 5min active talk) is comfortably met by this single battery alone for a fully-loaded 32-box system — real computed ~1.6x margin, see the block comment above. UL/ETL listed. $1,653.64 confirmed via BEC Audio 2026-07-28.',
};
export const AREA_OF_REFUGE_UPS_BATTERY = {
    model: 'RATH 1000B Add-On Battery',
    sku: '1000B',
    priceUsd: 843.90,
    imageUrl: null,
    note: 'A second 82Ah battery wired in parallel to the 1000 unit\'s included battery (RATH\'s own "extended runtime" wiring instructions) — real extra margin/redundancy, not required to meet the base 24hr-standby/5min-active-talk spec on a standard ≤32-box system (see AREA_OF_REFUGE_UPS\'s note above). No confirmed product photo found (BEC Audio\'s listing only shows the generic RATH/Avire brand logo, not the actual battery), so left blank rather than showing a placeholder. $843.90 confirmed via BEC Audio 2026-07-28.',
};
// Threshold from the BOSS 3400 datasheet math worked out in
// AREA_OF_REFUGE_UPS's comment above: a single included 82Ah battery covers
// a fully-loaded 32-box system (~1.6x margin); above 32 boxes the system
// needs the BOSS-3400-128 variant (74W draw), which needs BOTH batteries in
// parallel (164Ah, ~1.15x margin) to still meet the 24hr-standby/5min-talk
// spec. There's no confirmed separately-priced "3400-128" SKU at any real
// distributor — checked directly against BEC Audio's and BEC Integrated
// Solutions' own live shop listings 2026-07-28: both show the same generic
// "Rath Communications Smart Command BOSS Unit" product across the same
// $3,783–$3,961 range for either config, no second distinctly-priced
// listing. So this reuses AREA_OF_REFUGE_CONTROL_MODULE's existing price/
// object for both configurations, and instead flags "confirm before
// quoting" on the seeded row — same convention as every other distributor-
// sourced price in this file. Do not add a fabricated `..._128` price
// constant without first finding a real, separately-priced SKU.
const AREA_OF_REFUGE_LARGE_SYSTEM_THRESHOLD = 32;

// Builds the same 4-row Area of Refuge head-end seed addControlModuleToRack
// always adds (BOSS + Call Commander + UPS + battery), varying only the
// battery qty and a distributor-confirm note above the threshold. Pulled
// into one shared helper so both the admin builder's manual "+ Add RATH
// control module" button and the AI-suggested-sizing "Apply to rack
// builder" action (AdminProposalPage.js) stay byte-for-byte consistent —
// `stationCount` omitted/undefined reproduces today's exact qty:1 output.
export function areaOfRefugeControlModuleSeed(stationCount) {
    const isLarge = Number(stationCount) > AREA_OF_REFUGE_LARGE_SYSTEM_THRESHOLD;
    const confirmNote = ` CONFIRM current 3400-128 configuration/pricing with the distributor before quoting — ${stationCount} suggested stations exceeds the 32-box single-line capacity.`;
    return [
        {
            category: 'refuge_control', manufacturer: 'RATH', model: AREA_OF_REFUGE_CONTROL_MODULE.model,
            qty: 1, unit_cost: AREA_OF_REFUGE_CONTROL_MODULE.priceUsd, ru_height: null, port_count: null,
            notes: isLarge ? `${AREA_OF_REFUGE_CONTROL_MODULE.note}${confirmNote}` : AREA_OF_REFUGE_CONTROL_MODULE.note,
            image_url: AREA_OF_REFUGE_CONTROL_MODULE.imageUrl || '',
        },
        {
            category: 'refuge_control', manufacturer: 'RATH', model: AREA_OF_REFUGE_CALL_COMMANDER.model,
            qty: 1, unit_cost: AREA_OF_REFUGE_CALL_COMMANDER.priceUsd, ru_height: null, port_count: null,
            notes: AREA_OF_REFUGE_CALL_COMMANDER.note, image_url: AREA_OF_REFUGE_CALL_COMMANDER.imageUrl || '',
        },
        {
            category: 'ups', manufacturer: 'RATH', model: AREA_OF_REFUGE_UPS.model,
            qty: 1, unit_cost: AREA_OF_REFUGE_UPS.priceUsd, ru_height: null, port_count: null,
            notes: AREA_OF_REFUGE_UPS.note, image_url: AREA_OF_REFUGE_UPS.imageUrl || '',
        },
        {
            category: 'ups', manufacturer: 'RATH', model: AREA_OF_REFUGE_UPS_BATTERY.model,
            qty: isLarge ? 2 : 1, unit_cost: AREA_OF_REFUGE_UPS_BATTERY.priceUsd, ru_height: null, port_count: null,
            notes: isLarge
                ? `${AREA_OF_REFUGE_UPS_BATTERY.note} 2nd battery required above 32 stations (164Ah total) to meet the 24hr-standby/5min-talk spec at 128-box power draw (74W) — see AREA_OF_REFUGE_UPS's comment for the worked math.`
                : AREA_OF_REFUGE_UPS_BATTERY.note,
            image_url: AREA_OF_REFUGE_UPS_BATTERY.imageUrl || '',
        },
    ];
}

// Suggested Area of Refuge call-station sizing from AI-detected per-building
// specs (buildlab-parse-sitemap's `buildingsDetected` — one call station per
// elevator lobby per floor, BuildLab's real install convention, confirmed
// with staff as a deliberate house standard, NOT derived from IBC exit-count
// logic — do not "correct" this back toward exit-stairwell counts). Each
// building is sized independently and summed — a multi-building project
// with different floor counts per building must never be treated as one
// flat floor-count × lobby-count for the whole site.
export function aorSuggestedSizing(buildings) {
    const perBuilding = (buildings || []).map((b) => ({
        ...b,
        stationCount: (b.floorCount != null && b.elevatorLobbyCount != null)
            ? b.floorCount * b.elevatorLobbyCount : null,
    }));
    const totalStations = perBuilding.reduce((sum, b) => sum + (b.stationCount || 0), 0);
    return { perBuilding, totalStations, needsLargeConfig: totalStations > AREA_OF_REFUGE_LARGE_SYSTEM_THRESHOLD };
}

// Real RATH/Avire signage (BEC Audio, 2026-07-28) satisfying IBC 1009.6.5's
// two distinct requirements — a visual ID sign (ISA symbol + "AREA OF
// REFUGE" text) and a tactile/instructional sign (raised letters + Braille,
// ICC A117.1). Folded together as a per-location kit into DEVICE_RATES.
// refuge_station above rather than offered as a separate pick here — see
// that entry's comment for the exact math.
export const AREA_OF_REFUGE_SIGNAGE = [
    { key: '7041', model: 'Area of Refuge sign (photoluminescent)', sku: '7041', priceUsd: 75.40, note: 'Non-electric photoluminescent ID sign — ISA symbol + "AREA OF REFUGE" text, 8"x8". Confirmed via BEC Audio 2026-07-28.' },
    { key: '7049', model: 'Area of Refuge directions/instruction sign', sku: '7049', priceUsd: 62.35, note: 'Raised letters + Braille, peel-and-stick, mounts 48" AFF to top of sign — satisfies the tactile-signage (ICC A117.1) and posted-instructions (IBC 1009.6.4) requirements. Confirmed via BEC Audio 2026-07-28.' },
    { key: '7050', model: 'Area of Refuge sign (LED-illuminated)', sku: '7050', priceUsd: 374.10, note: 'Electrically illuminated alternative to 7041 (120/277VAC + battery backup) — use where exit-sign illumination is already required (IBC 1013.3), which then obligates the AoR sign to be illuminated too. Confirmed via BEC Audio 2026-07-28.' },
];

// Real RATH/Avire DIRECTIONAL/WAYFINDING signage — pointing occupants TOWARD
// the area of refuge from elsewhere on the floor (a corridor junction,
// direction change, or elevator lobby), distinct from the identification
// signage AT the door (AREA_OF_REFUGE_SIGNAGE above, already folded into
// DEVICE_RATES.refuge_station). Confirmed live via BEC Integrated Solutions,
// 2026-07-28. Previously scoped OUT of this calculator entirely (see
// CLAUDE.md's "Fire-rated cable and directional signage" note) — treated as
// the GC's own exit-signage package responsibility, since real quantity
// depends on a floor's actual corridor layout, not something derivable from
// placed call-station count the way the at-the-door kit is. Reversed
// 2026-07-29 per explicit staff direction ("AOR should also include the
// cost for all signages") — now priced here as a real, itemized add-on via
// directionalSignageCost() below (a staff-entered quantity calculator, same
// {price, text} shape coaxHomerunAlternate()/smcPanelAlternate() use, NOT an
// auto-computed default, since the real count is still genuinely
// project-specific).
export const AREA_OF_REFUGE_DIRECTIONAL_SIGNAGE = [
    { key: 'stairwell', model: 'Area of Refuge directional sign (stairwell)', sku: '7047L/R', priceUsd: 71.05, note: '5"x15" Blue w/Braille directional arrow sign, points occupants toward the area of refuge from a corridor or stairwell landing. Confirmed via BEC Integrated Solutions 2026-07-28.' },
    { key: 'elevator', model: 'Area of Refuge directional sign (elevator landing)', sku: '7047EL/ER', priceUsd: 71.05, note: 'Same directional sign line, elevator-landing variant. Confirmed via BEC Integrated Solutions 2026-07-28.' },
];
// Peel-and-stick or simple screw-mount — far less involved than an SMC panel
// (SMC_PANEL_INSTALL_LABOR = $45) or an in-unit device, so a smaller flat
// figure is defensible; a reasoned planning estimate, not a sourced number
// (no per-install-hour research exists for this task).
const DIRECTIONAL_SIGNAGE_INSTALL_LABOR = 20;

export function directionalSignageCost(quantity, key = 'stairwell') {
    const n = Math.max(0, Number(quantity) || 0);
    const opt = AREA_OF_REFUGE_DIRECTIONAL_SIGNAGE.find((o) => o.key === key) || AREA_OF_REFUGE_DIRECTIONAL_SIGNAGE[0];
    const unitPrice = opt.priceUsd * HARDWARE_MARKUP + DIRECTIONAL_SIGNAGE_INSTALL_LABOR;
    const price = Math.round(n * unitPrice);
    const text = `Add Alternate — Area of Refuge Directional/Wayfinding Signage (${opt.model}, ${opt.sku}): ${n}× sign${n === 1 ? '' : 's'} pointing occupants toward the area of refuge from elsewhere on the floor — supply and install. Distinct from the identification/tactile signage already included at each call station.`;
    return { quantity: n, model: opt.model, sku: opt.sku, unitPrice, price, text };
}

// ---------------------------------------------------------------------------
// EV Charging — its own standalone scope_type ('ev_charging', added
// 2026-07-30). Until this date, EV charging existed only as an "Add
// Alternate" a staff member could attach to whatever Data/Communications
// (networking) scope happened to be open — evChargingRecommendation()
// produced a {price, text} pair for the "+ Add as alternate" button, the
// same shape coaxHomerunAlternate()/smcPanelAlternate() still use for their
// own genuinely-optional add-ons. EV charging itself isn't optional add-on
// scope the way those two are (a project either has EV charger
// infrastructure to quote or it doesn't), so it now gets a real scope card
// of its own — evChargingRecommendation() below still computes the exact
// same real numbers, just as this scope's own price + description instead
// of an inserted alternate blurb. Real UniFi EV Station hardware (confirmed
// live on store.ui.com 2026-07-26 — prices/stock change constantly, same
// caveat as UNIFI_CATALOG, always re-verify before quoting a client).
// Deliberately its own small dataset rather than folded into
// UNIFI_CATALOG/RackCatalogPicker — an EV Station isn't rack-mounted
// equipment (it's a wall/pedestal-mounted charging unit) and isn't a
// floor-plan device with a coverage cone (no coverage cone to draw, unlike
// a camera/WAP), so neither existing hardware model fits; ev_charging is
// deliberately absent from both FLOORPLAN_SCOPE_TYPES and
// RACK_BUILDER_SCOPE_TYPES for the same reason.
// imageUrl on both entries below was the real store.ui.com "model viewer
// poster" hero shot (a clean, isolated-on-white angled product photo) —
// swapped 2026-07-31 from an earlier dimension/spec-diagram image (still a
// real product photo, just annotated with callout labels/measurements —
// fine for a technical reference, less so for a client-facing "here's what
// you're choosing" showcase).
//
// Swapped AGAIN 2026-07-31 (later the same day), per real staff feedback
// after seeing the gallery live: both "model viewer poster" shots turned out
// to be the exact same side-profile angle — charger unit + coiled cable,
// viewed from the side — indistinguishable from each other and from
// EV_CHARGER_STAND's own angled shot at a glance. Both now use a genuinely
// different angle pulled from store.ui.com's own real product photo gallery
// (not the 3D-model-viewer poster) — a straight-on front view showing the
// actual display face (Pro's 10.1" touch display / Lite's 1.7" status
// display, both mid-prompt: "Plug the charger into your vehicle to begin"),
// no cable in frame at all. Confirmed via `curl -sI` before use: Pro's front
// shot is 864,857 bytes / image/png / 1500×1500 (store.ui.com asset
// 12f7e09f-8c83-44a1-838e-e2e4ea4cc690); Lite's is 862,444 bytes / image/png
// / 1500×1500 (asset 93013df8-e1de-4003-b2b7-6452557df2ff). Both are square
// (vs. the earlier portrait 2249×3000/2159×3000 shots) — no imageZoom/crop
// tweak needed, EVChargingSelector.js's `.evsel__modelimg` and
// ExampleGallery.js both render with `object-fit:contain`, which letterboxes
// any aspect ratio rather than cropping it. EV_CHARGER_STAND/
// EV_CHARGER_LITE_STAND_ADAPTER/EV_CHARGING_CELLULAR_GATEWAY were
// spot-checked the same pass against their own real store.ui.com galleries
// and deliberately left unchanged — none of the three has this side-profile-
// plus-cable problem to begin with (a pedestal, a flat mounting bracket, and
// a router with antennas, none of which carry a coiled charging cable), and
// no alternate angle in any of their galleries was a clear improvement over
// the one already in use.
export const EV_CHARGER_OPTIONS = [
    {
        key: 'pro',
        model: 'UniFi EV Station Pro',
        sku: 'UC-EV-Station-Pro',
        priceUsd: 1199,
        powerKw: 11,
        display: '10.1" touch display',
        connectivity: ['Ethernet (1 GbE RJ45)', 'WiFi 802.11a/b/g/n/ac', 'Bluetooth 4.2'],
        imageUrl: 'https://cdn.ecomm.ui.com/products/0a9b39b2-a717-49f2-94e7-6abbee847417/12f7e09f-8c83-44a1-838e-e2e4ea4cc690.png',
        note: 'Sold out on store.ui.com as of 2026-07-26 — confirm stock before quoting.',
    },
    {
        key: 'lite',
        model: 'UniFi EV Station Lite',
        sku: 'UC-EV-Station-Lite',
        priceUsd: 499,
        powerKw: 11,
        display: '1.7" status display',
        connectivity: ['Ethernet (1 GbE RJ45)', 'WiFi', 'Bluetooth 5.0'],
        imageUrl: 'https://cdn.ecomm.ui.com/products/5c615a25-b1cf-4257-901a-99afbc15d62e/93013df8-e1de-4003-b2b7-6452557df2ff.png',
    },
];

// Pedestal/mounting accessory — real Ubiquiti hardware, compatible with both
// charger models. The Lite additionally needs a small stand adapter to fit
// the same stand (the Pro mounts to it directly).
// `dimensions` added 2026-07-31, sourced from ui.com's own EV Charging
// microsite "Installation Essentials" cards (confirmed against the live
// page, same as every other spec/copy string this file carries) — surfaced
// as a caption under these items' existing gallery photos in
// EVChargingSelector.js rather than a second "Installation Essentials"
// block, since the photos themselves were already covered there.
export const EV_CHARGER_STAND = {
    model: 'UniFi EV Station Stand',
    priceUsd: 399,
    imageUrl: 'https://cdn.ecomm.ui.com/products/6dea5399-d215-45a1-9b30-ae9fc640407a/e315835a-15d9-42f6-a9cc-2d91a2579eab.png',
    dimensions: '208 x 95 x 1277 mm (8.2 x 3.7 x 50.3")',
};
export const EV_CHARGER_LITE_STAND_ADAPTER = {
    model: 'EV Station Lite Stand Adapter',
    priceUsd: 29,
    imageUrl: 'https://cdn.ecomm.ui.com/products/8d28b331-ca68-4dac-b8d5-5c6b18684dde/987c9fe1-418d-487c-819a-fef609eb3130.png',
    dimensions: '209.7 x 207.3 x 89.5 mm (8.3 x 8.2 x 3.5")',
};

// Neither EV Station model has a built-in cellular modem (confirmed via
// techspecs.ui.com — both are GbE Ethernet + WiFi + Bluetooth only). A
// "Cellular" connectivity choice therefore isn't a charger hardware option —
// it means an external UniFi cellular gateway providing backhaul at a site
// with no wired/WiFi infrastructure nearby, with the charger then connecting
// to THAT gateway over its own Ethernet or WiFi as usual. Modeled honestly
// as real, separate hardware rather than an invented charger capability.
export const EV_CHARGING_CELLULAR_GATEWAY = {
    model: 'UniFi Mobile Router (UMR)',
    priceUsd: 199,
    // Real store.ui.com "model viewer poster" hero shot (isolated-on-white
    // angled product photo, same style as the two EV_CHARGER_OPTIONS
    // images above) — added 2026-07-31, confirmed via `curl -sI` before
    // use: 258,635 bytes / image/png / 2084×3000. This entry previously
    // had no imageUrl at all.
    imageUrl: 'https://cdn.ecomm.ui.com/products/e877092f-9fac-4a8a-919c-82380b395793/799d7856-47ec-47b8-9837-93b0ed489d50.png',
    note: 'Hardware price only — requires an AT&T nano-SIM data plan ($15–$70/mo, sold separately by Ubiquiti).',
};

export const EV_CHARGING_CONNECTIVITY = ['wired', 'wireless', 'cellular'];
export const EV_CHARGING_CONNECTIVITY_LABEL = { wired: 'Wired (Ethernet)', wireless: 'Wireless (WiFi)', cellular: 'Cellular (gateway)' };

// BuildLab's own installation labor — real staff direction 2026-07-31:
// "should calculate labor which includes electrical wiring, pedestal
// mounting, EV charger setup and configuration." Deliberately scoped to
// BuildLab's own low-voltage work — mounting the unit, running/terminating
// its network connection, and app-based setup/commissioning — NOT the
// 200–240V circuit (that stays the Electrical Sub's job per
// EV_CHARGING_ELECTRICAL_NOTE above; a low-voltage integrator doesn't bill
// labor for work outside its own trade). Planning-hour estimates at this
// codebase's standard $100/hr rate (same convention as the networking
// head-end labor rates and every other device-install labor figure in this
// file) — not a sourced industry standard, staff can override per project
// the same way every other labor figure here already is.
export const EV_CHARGER_INSTALL_LABOR = 250; // 2.5hr: unit mounting + low-voltage/network wiring & termination + app setup/configuration
export const EV_CHARGER_PEDESTAL_LABOR = 100; // 1hr: pedestal assembly, anchor, charger-to-pedestal mount — only when a stand is included

// Standing scope-boundary instruction — always shown as its own visible
// callout on both the admin scope card and every client-facing surface
// (NOT just baked into evChargingRecommendation()'s generated description
// text, which staff might never insert). Deliberately says "200–240V", not
// a specific amperage/breaker size (e.g. the earlier "240V/50A" wording
// this replaces) — Level 2 EV chargers run 208–240V, but the exact circuit
// amperage depends on which charger/site is ultimately installed and was
// never a real sourced figure for this app to assert.
export const EV_CHARGING_ELECTRICAL_NOTE = 'Electrical Sub to run stub-out conduits and 200–240V wiring to each charger location — this work is not included in this scope.';

// Builds a priced, itemized EV charging configuration — real hardware, real
// prices, no invented numbers. Returns {price, text} — `price` is the
// ev_charging scope's own price (AdminProposalPage.js's "Apply" button
// writes it straight to the scope's `price` column), `text` is a plain
// scope-description paragraph (no "Add Alternate —" prefix; it's the
// scope's own content now, not an inserted add-on blurb) staff can drop in
// via "Insert scope text". Config shape ({chargerKey, quantity,
// connectivity, includeStand}) matches ev_charging_config exactly, so the
// same object staff edits in the admin configurator (or a client edits via
// EVChargingSelector.js's own interactive picker, persisted through the
// update_ev_charging_selection RPC) round-trips straight to persistence and
// back into either side's render — one shared config, one shared function.
export function evChargingRecommendation({ chargerKey = 'lite', quantity = 1, connectivity = 'wired', includeStand = true } = {}) {
    const charger = EV_CHARGER_OPTIONS.find((o) => o.key === chargerKey) || EV_CHARGER_OPTIONS[1];
    const n = Math.max(1, Number(quantity) || 1);
    // HARDWARE_MARKUP applied per-item so the itemized lines shown to the
    // client always sum to the final `price` — not applied only at the end,
    // which would leave the displayed per-unit prices looking like real cost
    // while the total silently ran 20% ahead of them.
    const chargerUnit = charger.priceUsd * HARDWARE_MARKUP;
    let price = chargerUnit * n;
    const lines = [`${n}× ${charger.model} (${charger.sku}) — ${fmtMoney(chargerUnit)} each`];
    if (includeStand) {
        const standUnit = EV_CHARGER_STAND.priceUsd * HARDWARE_MARKUP;
        price += standUnit * n;
        lines.push(`${n}× ${EV_CHARGER_STAND.model} — ${fmtMoney(standUnit)} each`);
        if (charger.key === 'lite') {
            const adapterUnit = EV_CHARGER_LITE_STAND_ADAPTER.priceUsd * HARDWARE_MARKUP;
            price += adapterUnit * n;
            lines.push(`${n}× ${EV_CHARGER_LITE_STAND_ADAPTER.model} — ${fmtMoney(adapterUnit)} each`);
        }
    }
    if (connectivity === 'cellular') {
        const gatewayUnit = EV_CHARGING_CELLULAR_GATEWAY.priceUsd * HARDWARE_MARKUP;
        price += gatewayUnit;
        lines.push(`1× ${EV_CHARGING_CELLULAR_GATEWAY.model} — ${fmtMoney(gatewayUnit)} (${EV_CHARGING_CELLULAR_GATEWAY.note})`);
    }
    // Installation labor — not marked up (labor never carries HARDWARE_MARKUP
    // anywhere in this file), scales with quantity like every other
    // per-device labor figure. Pedestal labor only applies when a stand was
    // actually included above.
    price += EV_CHARGER_INSTALL_LABOR * n;
    lines.push(`${n}× Charger installation labor (mounting, low-voltage wiring & setup/configuration) — ${fmtMoney(EV_CHARGER_INSTALL_LABOR)} each`);
    if (includeStand) {
        price += EV_CHARGER_PEDESTAL_LABOR * n;
        lines.push(`${n}× Pedestal mounting labor — ${fmtMoney(EV_CHARGER_PEDESTAL_LABOR)} each`);
    }
    const text = `EV Charging (UniFi EV Charging Technology): ${lines.join('; ')}. Charger connectivity: ${EV_CHARGING_CONNECTIVITY_LABEL[connectivity]}.${charger.note ? ` Note: ${charger.note}` : ''} This price covers the charger hardware, installation labor, and network connectivity only. ${EV_CHARGING_ELECTRICAL_NOTE}`;
    return { charger, quantity: n, connectivity, includeStand, price: Math.round(price / 100) * 100, text, lines };
}

// Read-only reshape of a persisted ev_charging_config — same "returns null
// when cfg is absent" convention as cablingConfigBreakdown() above.
// EVChargingSelector.js (the interactive picker ClientProposalPage.js/
// ProposalSharePage.js both mount) seeds its own local, always-populated
// cfg from real defaults rather than relying on this null-safe wrapper
// directly — a client always gets a fully interactive picker, never a
// blank state — but calls this same function under the hood, so its live
// price/line-item output can never drift from evChargingRecommendation()'s
// real math.
export function evChargingConfigBreakdown(cfg) {
    if (!cfg) return null;
    return evChargingRecommendation(cfg);
}

// ---------------------------------------------------------------------------
// Comprehensive structured-cabling scope of work, itemized the way BuildLab
// actually builds it: MPOE → IDF backbone, IDF → unit SMC distribution, and
// SMC → room in-unit drops, with counts derived from the unit count. Bedroom
// mix isn't captured on the request, so a stated average is used — the text
// says so, and staff edit freely. (Used by auto-draft; the proposal builder's
// interactive takeoff via cablingTakeoff() supersedes it once staff answer
// the real questions.)
export function structuredCablingScopeText({ units, bedroomsPerUnit = 2 }) {
    const n = Number(units) || 0;
    const dropsPerUnit = bedroomsPerUnit + 1; // each bedroom + living room
    const inUnitDrops = n * dropsPerUnit;
    const lines = [
        'Scope of work:',
        `• Distribution — 1× fiber microduct pathway per unit, IDF → in-unit SMC enclosure${n ? ` (${n} pathways)` : ''}. Empty conduit only — fiber cable, blow-in, termination, and the ONT/media converter are NOT included; coordinate with the ISP or networking scope for activation.`,
        `• In-unit — Cat6 from the unit SMC media converter to each bedroom + living room${n ? ` (~${inUnitDrops} drops @ ${bedroomsPerUnit}BR avg)` : ''}; RG6 coax to living room`,
        '• MPOE build-out — demarc extension, grounding & bonding',
        '• Termination, labeling, and test & certification of every in-unit copper link; as-built documentation',
        `Assumes a ${bedroomsPerUnit}-bedroom average — refine counts from the actual unit mix.`,
    ];
    return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Mapping a request's `systems` strings (free-ish text from the public intake
// form OR hand-entered) to proposal scopes. Keyword-matched, not exact-matched,
// because the stored strings vary ("CCTV / video surveillance" vs
// "surveillance"). `rate` links to a SCOPES entry when a per-unit planning
// rate exists; systems with no defensible per-unit rate get price null (TBD).
// Word boundaries matter: without them, hand-entered strings false-positive
// ("dashboard" → /das/, "outdoor" → /door/, "gateway" → /gate/, "firewall"
// → /fire/). Systems matching NO pattern still become an 'other' TBD scope
// (see scopesFromRequest) — never silently dropped.
const SYSTEM_PATTERNS = [
    { re: /cabl/i, scope_type: 'structured_cabling', title: 'Structured Cabling', rate: 'cabling' },
    { re: /mdf|idf|it closet/i, scope_type: 'networking', title: 'IT Closet / MDF-IDF Build-Out', rate: null },
    // Matches both the current SYSTEMS option ('Data/Communications') and the
    // pre-2026-07-29 wording ('Resident Wi-Fi / managed network') still
    // sitting in older requests' stored systems arrays — never renamed those
    // rows, so both phrasings must keep resolving to the same scope.
    { re: /data\/?\s*comm|wi-?fi|network/i, scope_type: 'networking', title: 'Data/Communications', rate: 'wifi' },
    { re: /cctv|surveil|camera/i, scope_type: 'surveillance', title: 'Video Surveillance', rate: 'surveillance' },
    { re: /\baccess\b|\bdoors?\b|\bgates?\b/i, scope_type: 'access_control', title: 'Access Control', rate: 'access' },
    { re: /\bdas\b|errcs|erccs/i, scope_type: 'fire_life_safety', title: 'DAS / ERRCS (Public Safety Radio)', rate: null },
    // IBC 1009.6.5, same citation used everywhere else this system is
    // referenced in this file (see DEVICE_RATES.refuge_station and the
    // AREA_OF_REFUGE_* block below) — California's CBC 1009 adopts the same
    // section, so no distinction is lost by citing the base IBC section here.
    { re: /refuge/i, scope_type: 'area_of_refuge', title: 'Area of Refuge (IBC 1009.6.5)', rate: null },
    { re: /\bfire\b|life.?safety|\balarm\b/i, scope_type: 'fire_life_safety', title: 'Fire & Life Safety', rate: 'fls' },
];

const roundTo = (n, step) => Math.round(n / step) * step;

// Suggested price for one rate id given project facts — midpoint of the
// low–high planning range with the same multipliers computeBudget applies.
export function suggestScopePrice(rateId, { units, type = 'midrise', senior = false }) {
    const rate = SCOPES.find((s) => s.id === rateId);
    if (!rate || !units) return null;
    const typeMult = (BUILDING_TYPES.find((t) => t.v === type) || {}).mult || 1;
    const mult = typeMult * (senior ? SENIOR_MULT : 1) * volumeFactor(units);
    const low = rate.low * mult * units;
    const high = rate.high * mult * units;
    return { low, high, mid: roundTo((low + high) / 2, 100) };
}

// True when a request's building_type text indicates senior/supportive housing
// (the only rise-independent signal the intake form gives the rate model).
export const isSeniorHousing = (buildingTypeText) => /senior|supportive/i.test(buildingTypeText || '');

// Build ready-to-insert proposal scopes from a request row. Assumes mid-rise
// (the intake form doesn't capture stories); every price is a SUGGESTION the
// staff builder shows as editable, so the assumption is safe and visible.
export function scopesFromRequest(request) {
    const systems = Array.isArray(request.systems) ? request.systems : [];
    const units = Number(request.units) || 0;
    const senior = isSeniorHousing(request.building_type);
    const seen = new Set();
    const scopes = [];
    systems.forEach((sys) => {
        const match = SYSTEM_PATTERNS.find((p) => p.re.test(sys));
        // Unmatched systems (e.g. "Not sure yet — need a recommendation", or
        // any hand-entered string the patterns don't recognize) become an
        // 'other' TBD scope carrying the raw text — the client's ask must
        // survive into the draft, even unpriced.
        const title = match ? match.title : sys;
        if (seen.has(title)) return;
        seen.add(title);
        const suggestion = match?.rate ? suggestScopePrice(match.rate, { units, senior }) : null;
        let description = suggestion
            ? `Planning estimate ${fmtMoney(suggestion.low)}–${fmtMoney(suggestion.high)} for ${units} units${senior ? ' (senior/supportive housing)' : ''}, mid-rise assumed. Refine after site walk.`
            : `Requested: ${sys}. Pricing to be determined after review.`;
        // Structured cabling auto-drafts with the full itemized scope of work,
        // not just a price line.
        if (match?.rate === 'cabling') description += `\n\n${structuredCablingScopeText({ units })}`;
        scopes.push({
            scope_type: match ? match.scope_type : 'other',
            title,
            description,
            price: suggestion ? suggestion.mid : null,
            sort_order: scopes.length,
        });
    });
    return scopes;
}

export const fmtMoney = (n) => '$' + Math.round(n).toLocaleString('en-US');
